/**
 * @file Defines all types to be used throughout the app. There's also
 * validators, functions that take an object of unknown type and check whether
 * that object does conform to the expected type or not.
 */

import moment from 'moment'
import uniqBy from 'lodash.uniqby'
import { v1 as uuid } from 'uuid'

import * as env from '../env'
import * as u from '../utils'
// Imported from the file, not the `helpers` barrel: the barrel pulls in
// helpers.ts, which imports this module, and that closes a cycle.
// `commission.ts` imports nothing.

/**
 * Price Interface
 */

export interface Price {
  label: string
  value: number
}

export interface CityPrices {
  label?: string
  list: Price[]
}

export interface StatePrices {
  pricesByCity: CityPrices[]
}

export interface SalePrices {
  arizona: CityPrices
  california: StatePrices
  nevada: CityPrices
}

export type SaleTag = Readonly<Record<CustomerField, SalePrices>>

export const boolean = ((val: unknown) =>
  typeof val === 'boolean' || val === '') as unknown as boolean
export const number = ((val: unknown) =>
  typeof val === 'number') as unknown as number
export const string = ((val: unknown) =>
  typeof val === 'string') as unknown as string
/**
 * Defaults to `'no'`.
 */
export const YesNo = ((val: unknown) =>
  val === 'yes' || val === 'no') as unknown as 'yes' | 'no'
/**
 * Unlike `YesNo`, it's set to `'yes'` by default.
 */
export const NoYes = ((val: unknown) =>
  val === 'yes' || val === 'no') as unknown as 'yes' | 'no'
export const YesNoEmpty = ((val: unknown) =>
  val === 'yes' || val === 'no' || val === '') as unknown as 'yes' | 'no' | ''

export const Bit = ((val: unknown) =>
  val === 0 || val === 1 || val === '') as unknown as 0 | 1
/**
 * Special case validator, only really validates at read-time, after
 * sanitizing, and not at write time, like other validators, however, we are
 * only using this for `has_touched_customer_cost` which is not written into
 * outside of updateCustomer().
 */
export const StrToPrimitive = ((val: unknown) => {
  try {
    if (typeof val !== 'object' || val === null) {
      return false
    }
    for (const objVal of u.values(val)) {
      const isBool = boolean as unknown as (value: unknown) => boolean
      const isStr = string as unknown as (value: unknown) => boolean
      const isNumber = string as unknown as (value: unknown) => boolean

      const isValid = isBool(objVal) || isStr(objVal) || isNumber(objVal)

      if (!isValid) {
        return false
      }
    }
    return true
  } catch (e) {
    u.log(`strToPrimitive() -> `, {
      e,
      val,
    })
    return false
  }
}) as unknown as Record<string, u.Primitive>

export const sanitizeBoolean = (val: unknown): boolean => {
  if (val === 'no') return false
  if (val === 'yes') return true
  return !!val
}

export const sanitizeNumber = (val: unknown): number => {
  const finalVal = u.parseFloating(val || 0)
  return Number.isFinite(finalVal) ? finalVal : 0
}

export const sanitizeString = (val: unknown): string => {
  // Coerce falsy values to an empty string
  return String(val || '')
}

export const sanitizeNoYes = (val: unknown): typeof NoYes => {
  if (val === 'yes') return 'yes'
  if (val === 'no') return 'no'

  // Coerce empty strings / nulls to 'no'
  return 'yes'
}

export const sanitizeYesNo = (val: unknown): typeof YesNo => {
  if (val === 'yes') return 'yes'
  if (val === 'no') return 'no'

  // Coerce empty strings / nulls to 'no'
  return 'no'
}

export const sanitizeYesNoEmpty = (val: unknown): typeof YesNoEmpty => {
  if (val === 'yes') return 'yes'
  if (val === 'no') return 'no'

  return ''
}

export const sanitizeBit = (val: unknown): typeof Bit => {
  // Coerce empty strings / nulls to 0
  return val ? 1 : 0
}

export const sanitizeStrToPrimitive = (
  val: unknown,
): Readonly<Record<string, u.Primitive>> => {
  if (typeof val !== 'string') {
    return u.EMPTY_OBJ
  }

  try {
    const obj = JSON.parse(val)
    if (typeof obj !== 'object' || obj === null) {
      return u.EMPTY_OBJ
    }
    for (const [key, val] of u.entries(obj)) {
      const isPrimitive = ['boolean', 'number', 'string'].includes(typeof val)

      if (!isPrimitive) {
        delete obj[key]
      }
    }
    return obj
  } catch (e) {
    u.log('sanitizeStrToPrimitive()', {
      e,
      val,
    })
    return u.EMPTY_OBJ
  }
}

export const fieldTypeToSanitizer = {
  [Bit.toString()]: sanitizeBit,
  [StrToPrimitive.toString()]: sanitizeStrToPrimitive,
  [YesNo.toString()]: sanitizeYesNo,
  [NoYes.toString()]: sanitizeNoYes,
  [YesNoEmpty.toString()]: sanitizeYesNoEmpty,
  [boolean.toString()]: sanitizeBoolean,
  [number.toString()]: sanitizeNumber,
  [string.toString()]: sanitizeString,
} as const

/**
 * Defines in runtime the Customer type for use in validation.
 */
export const CustomerSchema = {
  //#region id
  id: string,
  keyID: string,
  /**
   * Injected by Algolia, similar to key. Defined here to simplify types
   * elsewhere.
   */
  objectID: string,
  /**
   * This key is not actually in firebase but it should be added immediately
   * after obtaining the data from it, it is included here so as to only have
   * one type definition.
   */
  key: string,
  //#endregion id

  //#region essentialFields
  buildingType: string,
  has_touched_customer_cost: StrToPrimitive,
  has_touched_cost: StrToPrimitive,
  locked_price: StrToPrimitive,
  deleted: boolean,
  /**
   * When true, the rep's commission is not reduced by the 25/75 split above
   * the threshold — they get full commission on this deal regardless of
   * finalCommission. Per-deal exception (Jaron, Slack, Jul 2026), not a
   * threshold change.
   */
  commission_no_split: boolean,
  /**
   * Stamped at creation (12953417291) and still written, so app builds older
   * than the B166 fix show new deals right. NOT READ by current code: the
   * threshold comes from the deal's birth date - `splitThresholdForDeal` in
   * `helpers/commission.ts`. `stampReaders.test.ts` fails if anything reads it.
   */
  commission_split_threshold: number,
  /**
   * Same as above for the Arizona $1,200 lead fee: written at birth, not read.
   * `leadFeeStampForDeal` decides from the birth date.
   */
  lead_fee_stamped: number,
  /**
   * The ONLY per-deal exception to the birth-date rules (B166), in ms: this
   * deal follows the split and lead fee of that date instead of its birth
   * date. 0 = none. Set on purpose (script with preimage), never by the birth
   * path. `commission_no_split` still wins over everything.
   */
  commission_rules_date: number,
  efficiency_updated: string,
  /**
   * Closer
   */
  homeRep: string,
  /**
   * Generated server-side in firebase using `database.ServerValue.TIMESTAMP`.
   */
  createdAt: number,
  date: number,
  /**
   * A direct pointer to the house picture, shown in several places therefore
   * avoiding too many subscriptions to the customer's media.
   */
  owner_house: string,
  sharedContract: string,
  // #endregion essentialFields

  //#region customerData
  /**
   * `'Solar'|'HIL'|'RC'`.
   */
  products: string,
  crm_id: string,
  solarEmail: string,
  ssn: string,
  /**
   * Date of birth. Jaron asked for it on 2026-08-25 with an arrow into the gap
   * between SSN and State on Customer Info, so it sits beside SSN and gets the
   * same treatment: personal data, kept off anything customer-facing unless
   * someone asks for it explicitly. Free text rather than a date type, which
   * is how every other input on that form stores its value — and it means an
   * existing customer simply has it empty, with no migration.
   */
  dob: string,
  /**
   * Setter
   */
  solarRep: string,
  homeStoryNumber: string,
  yearHomeBuilt: string,
  globalNotes: string,
  customerAddress: string,
  addressLat: number,
  addressLng: number,
  customerEmail: string,
  customerName: string,
  customerPhone: string,
  customerPhoneAlt: string,
  /**
   * This is just the state pretty much at this point, certain old customers
   * might have `RC` or `CPS_HIL` (for Home Improvement Loan) but that is now
   * deprecated. See `contractCompany`.
   */
  solarCompany: string,
  /**
   * This used to be only used for MPU, then we created another one for S-MPU
   * (which is now removed). Since the installation company would of course be
   * the same, we are now making it a main field, and will be shared by all
   * efficiencies.
   */
  main_panel_upgrade_installation_company: string,
  /**
   * Used by Nirvana for both inverter_swap and ac_coupling efficiencies.
   */
  main_panel_location: string,
  /**
   * Equivalent to "last modified". This number will always be negative. The
   * lower this number, the most recently modified this record is.
   */
  sort_key: number,
  /**
   * The company name that will appear in the contract. It can even be ROOFNOW
   * for selling solar so this is arbitrary.
   */
  contractCompany: string,
  /**
   * Short name of the company whose logo the report COVER shows, chosen
   * independently of `contractCompany` (the one on the contract pages). Empty
   * (the default) keeps the cover logo the app derives on its own; any value is
   * either a `contractCompanies` short name or a cover-only brand (see
   * `coverCompanies.ts`).
   */
  coverCompany: string,
  // #endregion customerData

  //#region report
  signature: string,

  report_address: string,
  report_amount: string,
  report_app_id: string,
  report_company_signature: string,
  report_company_signature_date: number,
  report_contractor_name: string,
  report_customer_name: string,

  report_nirvana_finance: string,
  report_nirvana_upfront: string,
  report_nirvana_remaining: string,

  /**
   * The customer name as it appears on the report to be signed.
   */
  c_name: string,
  c_signature: string,
  c_signature_backup: string,
  c_signature_date: number,
  r_signature: string,
  /**
   * Used in report.
   */
  release_liability_date: number,
  // #endregion report

  //#region airConditioner
  airConditioner: boolean,
  air_conditioner: YesNo,
  air_conditioner_cost: string,
  air_conditioner_current_tons: string,
  air_conditioner_customer_cost: string,
  air_conditioner_extra_notes: string,
  air_conditioner_furnance: YesNo,
  air_conditioner_new_tons: string,
  /** How many AC units, multiplies the auto price (Jaron, 13312379361) */
  air_conditioner_qty: number,
  air_conditioner_notes: string,
  air_conditioner_replace_ducts: YesNo,
  air_conditioner_show: Bit,
  air_conditioner_swamp_cooler_use_remove: YesNo,
  air_conditioner_swamp_cooler_use: YesNo,
  air_conditioner_unit_type: string,
  air_conditioner_working: YesNo,
  //#endregion

  //#region atticInsulation
  attic_insulation: YesNo,
  attic_insulation_cost: string,
  atticInsulation: boolean,
  attic_insulation_customer_cost: string,
  attic_insulation_how_much: string,
  attic_insulation_levels_type: string,
  attic_insulation_notes: string,
  attic_insulation_extra_notes: string,
  attic_insulation_show: Bit,
  attic_square_footage: string,
  attic_insulation_type: string,
  //#endregion

  //#region atticFan
  atticFan: boolean,
  attic_fan: YesNo,
  attic_fan_cost: string,
  attic_fan_customer_cost: string,
  attic_fan_notes: string,
  attic_fan_extra_notes: string,
  attic_fan_quantity: string,
  attic_fan_show: Bit,
  //#endregion

  //#region battery
  battery: YesNo,
  battery_type: string,
  battery_size: string,
  battery_cost: string,
  battery_customer_cost: string,
  battery_notes: string,
  battery_extra_notes: string,
  battery_show: Bit,
  //#endregion

  //#region batteryMultiple
  battery_multiple: YesNo,
  /**
   * This used to be bank location, but now it represents just the battery type.
   * Regardless of indoors/outdoors.
   */
  battery_multiple_bank_location: string,
  battery_multiple_customer_material: YesNo,
  battery_multiple_distance_to_main_panel: string,
  // TODO: change to Qty
  battery_multiple_how_many: number,
  battery_multiple_cost: string,
  battery_multiple_customer_cost: string,
  battery_multiple_notes: string,
  battery_multiple_extra_notes: string,
  battery_multiple_show: Bit,
  //#endregion

  //#region canLightNew
  can_light_new: YesNo,
  can_light_new_how_many: number,
  can_light_new_will_remove: YesNo,
  can_light_new_will_remove_how_many: number,
  can_light_new_high_ceiling: YesNo,
  can_light_new_notes: string,
  can_light_new_extra_notes: string,
  can_light_new_cost: string,
  can_light_new_customer_cost: string,
  can_light_new_show: Bit,
  //#endregion

  //#region canLight
  can_light: YesNo,
  can_light_detail: string,
  can_light_notes: string,
  can_light_extra_notes: string,
  can_light_cost: string,
  can_light_customer_cost: string,
  can_light_show: Bit,
  //#endregion

  //#region cash
  cash: YesNo,
  cashCheck: boolean,
  cash_air_conditioner: string,
  cash_amount: string,
  cash_artificial_turf: string,
  cash_cost: string,
  cash_customer_cost: string,
  cash_efficient_boilers: string,
  cash_efficient_water_heaters: string,
  cash_floor_insulation: string,
  cash_geothermal_heat_pumps: string,
  cash_heat_pumps: string,
  cash_irrigation_systems: string,
  cash_notes: string,
  cash_on_demand_or_tankless_hot: string,
  /**
   * A stringified object.
   */
  cash_options: string,
  cash_pool_pumps: string,
  cash_radiant_floors: string,
  cash_show: Bit,
  cash_solar_water_heaters: string,
  cash_water_saving_fixtures: string,
  cash_whole_house_fans_or_ventilation: string,
  cash_windows: string,
  //#endregion

  //#region ceilingFans
  ceiling_fan: YesNo,
  ceiling_fan_detail: string,
  ceiling_fan_cost: string,
  ceiling_fan_customer_cost: string,
  ceiling_fan_notes: string,
  ceiling_fan_extra_notes: string,
  ceiling_fan_show: Bit,
  //#endregion

  //#region constructionWork
  construction_work: YesNo,
  construction_work_cost: string,
  construction_work_customer_cost: string,
  construction_work_notes: string,
  construction_work_extra_notes: string,
  construction_work_show: Bit,
  //#endregion

  //#region derate
  derate: YesNo,
  derateShow: boolean,
  derate_cost: string,
  derate_extra_notes: string,
  derate_customer_cost: string,
  derate_notes: string,
  derate_show: Bit,
  //#endregion

  //#region ductInsulation
  ductInsulation: boolean,
  duct_insulation: YesNo,
  duct_insulation_cost: string,
  duct_insulation_customer_cost: string,
  duct_insulation_notes: string,
  duct_insulation_show: Bit,
  //#endregion

  //#region ductRepair
  ductRepair: boolean,
  duct_repair: YesNo,
  duct_repair_cost: string,
  duct_repair_customer_cost: string,
  duct_repair_notes: string,
  duct_repair_quantity: string,
  duct_repair_show: Bit,
  //#endregion

  //#region ductReplacement
  ductReplacement: boolean,
  duct_replacement: YesNo,
  duct_replacement_qty: number,
  duct_replacement_cost: string,
  duct_replacement_customer_cost: string,
  duct_replacement_notes: string,
  duct_replacement_show: Bit,
  //#endregion

  //#region ductSeal
  ductSeal: boolean,
  duct_seal: YesNo,
  duct_seal_cost: string,
  duct_seal_customer_cost: string,
  duct_seal_notes: string,
  duct_seal_extra_notes: string,
  duct_seal_show: Bit,
  //#endregion

  //#region flatRoofPanels
  flat_roof_panels: YesNo,
  flatRoofPanels: boolean,
  flat_roof_panels_show: Bit,
  flat_roof_panels_customer_cost: string,
  flat_roof_panels_cost: string,
  flat_roof_panels_how_many: string,
  flat_roof_panels_notes: string,
  flat_roof_panels_extra_notes: string,
  //#endregion

  //#region glow
  glow: YesNo,
  glow_price_per_watt: string,
  glow_power_output_mw: string,
  glow_cost: string,
  glow_customer_cost: string,
  glow_notes: string,
  glow_extra_notes: string,
  glow_show: Bit,
  //#endregion

  //#region inverterSwap
  inverter_swap: YesNo,
  inverter_swap_qty: number,
  inverter_swap_cost: string,
  inverter_swap_customer_cost: string,
  inverter_swap_customer_material: YesNo,
  inverter_swap_distance_to_mpu: string,
  inverter_swap_size: string,
  inverter_swap_notes: string,
  inverter_swap_extra_notes: string,
  inverter_swap_show: Bit,
  //#endregion

  //#region homeRun
  home_run: YesNo,
  home_run_ft: string,
  home_run_cost: string,
  home_run_customer_cost: string,
  home_run_notes: string,
  home_run_extra_notes: string,
  home_run_show: Bit,
  //#endregion

  //#region miniSplit
  miniSplit: boolean,
  mini_split: YesNo,
  mini_split_cost: string,
  mini_split_customer_cost: string,
  mini_split_ton1: string,
  mini_split_ton2: string,
  mini_split_tons: string,
  mini_split_notes: string,
  mini_split_extra_notes: string,
  mini_split_show: Bit,
  //#endregion

  //#region newDucts
  newDucts: boolean,
  new_ducts: YesNo,
  new_ducts_cost: string,
  new_ducts_customer_cost: string,
  new_ducts_description: string,
  new_ducts_extra_notes: string,
  new_ducts_notes: string,
  new_ducts_quantity: string,
  new_ducts_show: Bit,
  new_ducts_replace: YesNo,
  //#endregion

  //#region newWindows
  newWindows: boolean,
  new_windows: YesNo,
  new_windows_areas: string,
  new_windows_color: string,
  new_windows_cost: string,
  new_windows_grid: YesNo,
  new_windows_sqft_each_window: string,
  new_windows_customer_cost: string,
  new_windows_notes: string,
  new_windows_extra_notes: string,
  new_windows_quantity: string,
  new_windows_show: Bit,
  new_windows_sliding_glass: YesNo,
  new_windows_sliding_glass_size: string,
  /**
   * Actually linear feet. Mistake when initially named.
   */
  new_windows_sliding_glass_sqft: string,
  new_windows_sliding_glass_how_many: string,
  new_windows_california_city: string,
  new_windows_whole_house_custom: string,
  new_windows_sqft: string,
  new_windows_replace: YesNoEmpty,
  new_windows_replace_custom: string,
  //#endregion

  //#region nirvanaPanels
  nirvana_panels: YesNo,
  nirvana_panels_panel_scenario: string,
  nirvana_panels_distance_to_main_panel: string,
  nirvana_panels_how_many: number,
  nirvana_panels_notes: string,
  nirvana_panels_extra_notes: string,
  nirvana_panels_cost: string,
  nirvana_panels_customer_cost: string,
  nirvana_panels_show: Bit,
  //#endregion

  //#region outlet
  outlet: YesNo,
  outlet_detail: string,
  outlet_cost: string,
  outlet_customer_cost: string,
  outlet_notes: string,
  outlet_extra_notes: string,
  outlet_show: Bit,
  //#endregion

  //#region poolPump
  poolPump: boolean,
  pool_pump: YesNo,
  pool_pump_cost: string,
  pool_pump_custom: string,
  pool_pump_customer_cost: string,
  pool_pump_notes: string,
  pool_pump_show: Bit,
  //#endregion

  //#region protection
  protection: YesNo,
  protection_which: string,
  protection_cost: string,
  protection_customer_cost: string,
  protection_notes: string,
  protection_extra_notes: string,
  protection_show: Bit,
  //#endregion

  //#region roof
  roof: YesNo,
  roofCheck: boolean,
  /**
   * Nirvana only.
   */
  roof_shingles_or_tiles: string,
  roof_california_city: string,
  roof_cost: string,
  roof_fascia_included: YesNo,
  roof_fascia_included_square_footage: string,
  roof_pitch: string,
  roof_plywood_under_tile: YesNo,
  roof_plywood_replaced: YesNo,
  roof_plywood_replaced_square: string,
  roof_plywood_custom_roughly: string,
  roof_layover_or_tear: string,
  roof_tear_material: string,
  roof_patio_included: YesNo,
  roof_patio_how_many: string,
  roof_customer_cost: string,
  roof_layers_how_many: string,
  roof_square_footage: string,
  roof_will_add_license: NoYes,
  roof_commission_add_up: string,
  roof_extra_notes: string,
  roof_notes: string,
  roof_show: Bit,
  //#endregion

  //#region serviceAndRepairs
  service_and_repair: YesNo,
  service_and_repair_hours: string,
  service_and_repair_cost: string,
  service_and_repair_customer_cost: string,
  service_and_repair_notes: string,
  service_and_repair_extra_notes: string,
  service_and_repair_show: Bit,
  //#endregion

  //#region smallSystem
  small_system: YesNo,
  smallSystem: boolean,
  small_system_size: string,
  small_system_extra_batteries_qty: number,
  small_system_extra_batteries_will: YesNo,
  small_system_show: Bit,
  small_system_customer_cost: string,
  small_system_cost: string,
  small_system_notes: string,
  small_system_extra_notes: string,
  //#endregion

  //#region smartThermostat
  smartThermostat: boolean,
  smart_thermostat: YesNo,
  smart_thermostat_cost: string,
  smart_thermostat_customer_cost: string,
  smart_thermostat_how_many: string,
  smart_thermostat_notes: string,
  smart_thermostat_extra_notes: string,
  smart_thermostat_show: Bit,
  //#endregion

  //#region solarTax
  solar_tax: YesNo,
  solar_tax_cost: string,
  solar_tax_customer_cost: string,
  solar_tax_notes: string,
  solar_tax_show: Bit,
  //#endregion

  //#region mainPanelUpgrade
  main_panel_upgrade: YesNo,
  main_panel_upgrade_rating: string,
  main_panel_upgrade_cost: string,
  main_panel_upgrade_customer_cost: string,
  main_panel_upgrade_notes: string,
  main_panel_upgrade_extra_notes: string,
  main_panel_upgrade_show: Bit,
  main_panel_upgrade_needed_or_requested: string,
  //#endregion

  //#region panelRemoval
  panelRemoval: boolean,
  panel_removal: YesNo,
  panel_removal_how_many: number,
  panel_removal_cost: string,
  panel_removal_customer_cost: string,
  panel_removal_notes: string,
  panel_removal_extra_notes: string,
  panel_removal_show: Bit,
  //#endregion

  //#region nirvanaCheckList
  /**
   * Home Owners information ( Full Name, Address and Email)
   */
  checklist_nirvana_home_owners_info: YesNo,
  checklistNirvanaHomeOwnersInfo: boolean,

  checklist_nirvana_product_pics: YesNo,
  checklistNirvanaProductPics: boolean,

  checklist_nirvana_main_panel_pics: YesNo,
  checklistNirvanaMainPanelPics: boolean,

  checklist_nirvana_main_panel_inside_pics: YesNo,
  checklistNirvanaMainPanelInsidePics: boolean,

  checklist_nirvana_utility_bill: YesNo,
  checklistNirvanaUtilityBill: boolean,
  //#endregion

  //#region checkList
  attach_thermal_camera: YesNo,
  attachThermalCamera: boolean,
  //
  take_picture_of_home: YesNo,
  get_debts_report: YesNo,
  takePictureOfHome: boolean,
  perform_energy_assessment: YesNo,
  performEnergyAssessment: boolean,
  /**/ use_thermal_camera: YesNo,
  useThermalCamera: boolean,
  /**/ take_quiz: YesNo, // Now called survey
  takeQuiz: boolean,
  /**/ use_room_that_gets_hot: YesNo,
  useRoomThatGetsHot: boolean,
  /**/ use_smoke_pen: YesNo,
  useSmokePen: boolean,
  //
  take_pictures: YesNo,
  takePictures: boolean,
  //
  fill_out_prices: YesNo,
  fillOutPrices: boolean,
  //
  verify_pricing_or_ppw: YesNo,
  verifyPricingOrPpw: boolean,
  //
  show_ecohome_report: YesNo,
  showEcohomeReport: boolean,
  //
  show_solo_report: YesNo,
  showSoloReport: boolean,
  /**/ /**/ run_credit: YesNo,
  runCredit: boolean,
  //
  change_to_correct_email: YesNo,
  changeToCorrectEmail: boolean,
  //
  sign_financing_agreement: YesNo,
  signFinancingAgreement: boolean,
  /**/ sign_cosigner: YesNo,
  signCosigner: boolean,
  system_size: string,
  sub_panel_upgrade: YesNo,
  sub_panel_upgrade_cost: string,
  sub_panel_upgrade_customer_cost: string,
  sub_panel_upgrade_notes: string,
  sub_panel_upgrade_show: Bit,
  submit_install_docs_in_solo: YesNo,
  submitInstallDocsInSolo: boolean,
  /**/ counter_sign_rep_install_doc: YesNo,
  counterSignRepInstallDoc: boolean,
  /**/ sign_customer_install_docs: YesNo,
  signCustomerInstallDocs: boolean,
  //
  ecohome_report: YesNo,
  ecohomeReport: boolean,
  /**/ make_sure_ecohome_and_solo_match: YesNo,
  makeSureEcohomeAndSoloMatch: boolean,
  /**/ take_picture_of_utility_bill: YesNo,
  /**/ takePictureOfUtilityBill: boolean,
  //
  /**/ take_picture_of_id: YesNo,
  takePictureOfId: boolean,
  //
  /**/ take_pictures_of_meter: YesNo,
  takePicturesOfMeter: boolean,
  /**/ submit_detailed_notes_for_efficiencies: YesNo,
  submitDetailedNotesForEfficiencies: boolean,
  /**/ review_notes_with_customers: YesNo,
  reviewNotesWithCustomers: boolean,
  /**/ sign_ecohome_report: YesNo,
  signEcohomeReport: boolean,
  /**/ get_google_review_and_or_pic_with_customer: YesNo,
  getGoogleReviewAndOrPicWithCustomer: boolean,
  /**/ take_pic_with_customer: YesNo,
  takePicWithCustomer: boolean,
  //
  /**/
  offer_incentives_for_referrals: YesNo,
  offerIncentivesForReferrals: boolean,
  /**/
  //
  update_notes_on_HL: YesNo,
  updateNotesOnHl: boolean,
  //
  report_sale_to_slack: YesNo,
  reportSaleToSlack: boolean,
  // #endregion checkList

  //#region HIL
  hil_completion_client_sign: string,
  hil_completion_client_sign_date: number,
  hil_printed_name: string,
  //#endregion HIL

  //#region debts
  consolidationDebts: string,
  debtsPayments: string,
  collections: string,
  // #endregion debts

  //#region roofClaimingInputs
  //---- Main Page inputs ----
  /** Scoped roof signing; never reclassifies a legacy primary agreement. */
  roof_claiming_flow_version: string,
  roof_claiming_signed_email_delivery: string,
  roof_third_party_policyholder: string,
  roof_third_party_address: string,
  roof_third_party_policy_number: string,
  roof_third_party_claim_number: string,
  roof_third_party_company: string,
  roof_third_party_sign: string,
  roof_third_party_sign_date: number,
  roof_claiming_date: number,
  roof_claiming_homeowner: string,
  roof_claiming_phone: string,
  roof_claiming_phone_alt: string,
  roof_claiming_email: string,
  roof_claiming_address: string,
  roof_claiming_city: string,
  roof_claiming_zip: string,
  roof_claiming_sum: string,
  roof_claiming_deductible: string,
  roof_claiming_shingle_manufacturer: string,
  roof_claiming_style: string,
  roof_claiming_color: string,
  roof_claiming_drip_color: string,

  roof_claiming_age: string,
  roof_claiming_layers: string,
  roof_claiming_predominate_pitch: string,

  roof_claiming_pipe_jack: string,
  roof_claiming_chimney_flashing: string,
  roof_claiming_chimney_cap: string,
  roof_claiming_digital_satellite: string,
  roof_claiming_gas_cap: string,
  roof_claiming_other_one: string,

  roof_claiming_turtle_vent: string,
  roof_claiming_ridge_vent: string,
  roof_claiming_turbine_vent: string,
  roof_claiming_power_attic_vent: string,
  roof_claiming_other_two: string,
  roof_claiming_other_three: string,
  //---- End Main Page inputs ----
  //---- Main Page checkbox ----
  roof_claiming_tear_off_layers_shingles: Bit,
  roof_claiming_install_felt: Bit,
  roof_claiming_close_valleys: Bit,
  roof_claiming_ridges_color_coordinated: Bit,
  roof_claiming_install_new_flashings: Bit,
  roof_claiming_replace_ventilation: Bit,
  roof_claiming_install_nails: Bit,
  roof_claiming_clean_job_waste_gutters: Bit,
  roof_claiming_two_year_workmanship_warranty: Bit,
  roof_claiming_magnetic_sweep_property: Bit,

  roof_claiming_notes: string,
  roof_claiming_documents_notes: string,

  roof_claiming_date_loss: number,
  roof_claiming_insurance_company: string,
  roof_claiming_claim: string,

  roof_claiming_client_sign: string,
  roof_claiming_client_sign_date: number,
  roof_claiming_company_rep_sign: string,
  roof_claiming_company_rep_sign_date: number,

  roof_claiming_printed_name: string,
  roof_claiming_completion_client_sign: string,
  roof_claiming_completion_client_sign_date: number,

  roof_claiming_company_contractor: string,
  // #endregion roofClaimingInputs

  //#region securityMisc
  security_alarm_payout: string,
  /**
   * Flat $9.99/mo add-on, billed separately from the financed equipment
   * total — does NOT factor into `grandTotalWDF`/commission math. Confirmed
   * flat rate for everyone (Maria G, 2026-07-09). Defaults to 'no'.
   */
  security_monitoring_enabled: YesNo,
  /**
   * @type {PayMethodApplied}
   */
  security_loan_applied: string,
  /**
   * `security_loan_applied` will now contain the values even before applying,
   * for persistence, and this field tracks the application.
   */
  payment_is_applied: boolean,
  //#endregion

  //#region securityCams
  security_cams: YesNo,
  /**
   * RowableInput.
   */
  security_cams_detail: string,
  security_cams_is_6: string,
  security_cams_cost: string,
  security_cams_customer_cost: string,
  security_cams_notes: string,
  security_cams_extra_notes: string,
  security_cams_show: Bit,
  //#endregion securityCams

  //#region securitySystem
  security_system: YesNo,
  security_system_detail: string,
  security_system_cost: string,
  security_system_customer_cost: string,
  security_system_notes: string,
  security_system_extra_notes: string,
  security_system_show: Bit,
  //#endregion

  //#region securityTV
  security_tv: YesNo,
  security_tv_detail: string,
  security_tv_cost: string,
  security_tv_customer_cost: string,
  security_tv_notes: string,
  security_tv_extra_notes: string,
  security_tv_show: Bit,
  //#endregion

  //#region securityWifiCameras
  security_wifi_cameras: YesNo,
  /**
   * RowableInput.
   */
  security_wifi_cameras_detail: string,
  security_wifi_cameras_cost: string,
  security_wifi_cameras_customer_cost: string,
  security_wifi_cameras_notes: string,
  security_wifi_cameras_extra_notes: string,
  security_wifi_cameras_show: Bit,
  //#endregion securityWifiCameras

  //#region securityHomeAutomation
  security_home_automation: YesNo,
  /**
   * RowableInput.
   */
  security_home_automation_detail: string,
  security_home_automation_cost: string,
  security_home_automation_customer_cost: string,
  security_home_automation_notes: string,
  security_home_automation_extra_notes: string,
  security_home_automation_show: Bit,
  //#endregion securityHomeAutomation

  //#region securityContract
  security_contract_co_buyer: string,
  security_contract_sign_company: string,
  security_contract_sign_company_date: number,
  security_contract_sign_customer: string,
  security_contract_sign_customer_date: number,
  /** Timestamp set only after the signed-contract email request succeeds. */
  contract_signed_email_sent_at: number,
  /** Atomic receipt for guided signing email, persisted separately from contract fields. */
  contract_signed_email_delivery: string,
  /**
   * Separate signature block on the FirstElectricContract's own Notice of
   * Cancellation page — distinct from security_contract_sign_customer above
   * (the main contract signature).
   */
  security_contract_cancel_sign_customer: string,
  security_contract_cancel_sign_customer_date: number,
  /**
   * Editable company fields for the "Other (Cash)" / "Other (Financing)"
   * contract company options — used instead of the shared `ContractCompanies`
   * Firebase node when the rep needs a one-off company not preconfigured
   * there. Logo is stored as base64, same pattern as signatures.
   */
  other_contract_company_name: string,
  other_contract_company_address: string,
  other_contract_company_phone: string,
  other_contract_company_license: string,
  other_contract_company_logo: string,
  //#endregion

  //#region homeImprovementAgreement
  /**
   * T41 — Caballero's Electric LLC "Home Improvement Agreement" contract
   * (EV Charger / Generator / Smart Home System / Home Performance).
   * Independent multi-select checkboxes — not mutually exclusive (Jaron:
   * "Home Performance" can apply alongside "Smart Home System").
   */
  home_improvement_scope_ev_charger: YesNo,
  home_improvement_scope_generator: YesNo,
  home_improvement_scope_smart_home: YesNo,
  // "A rep has clicked the Smart Home System checkbox" — needed because that
  // box is ticked BY DEFAULT on the MSE contract (Jaron, 2026-09-01) and a
  // plain `YesNo` cannot tell "untouched" from "explicitly unticked" (both
  // sanitize to 'no'; 175 existing customers already carry a default 'no').
  // 'yes' here = honour `home_improvement_scope_smart_home`; anything else =
  // show ticked regardless. Set only by that checkbox's own onClick.
  home_improvement_scope_smart_home_touched: YesNo,
  home_improvement_scope_home_performance: YesNo,
  home_improvement_scope_other: string,
  home_improvement_project_scope_description: string,
  home_improvement_contract_price: string,
  home_improvement_add_service_price: string,
  home_improvement_first_payment: string,
  home_improvement_second_payment: string,
  home_improvement_installation_date: string,
  /**
   * Manual "Monthly: $___ /mo" line in the MSE/CE SCOPE OF WORK (13213659622).
   * Free text typed by the rep; display only, feeds no calculation.
   */
  home_improvement_monthly_payment: string,
  /**
   * "A rep edited this by hand" flags for the three totalCost-seeded payment
   * fields above. Set only by an edit control's onChange, never by the
   * seeding effect. The MSE seed effect leaves a field alone once its flag is
   * 'yes' (or the contract is signed — see §V). 'no' / absent = still
   * auto-tracking the contract total. Dormant until an edit control for these
   * fields exists again (none since the SYSTEM PRICING box was removed in
   * 8288f946), but the seed effect already honours them.
   */
  home_improvement_contract_price_touched: YesNo,
  home_improvement_first_payment_touched: YesNo,
  home_improvement_second_payment_touched: YesNo,
  /**
   * Owner 2 is optional — signature block only renders if this is filled in.
   */
  home_improvement_owner2_name: string,
  home_improvement_sign_owner1: string,
  home_improvement_sign_owner1_date: number,
  home_improvement_sign_owner2: string,
  home_improvement_sign_owner2_date: number,
  home_improvement_sign_company: string,
  home_improvement_sign_company_date: number,
  /**
   * Separate signature block on the Notice of Cancellation page itself (page
   * 6 of the PDF) — distinct from the main contract signatures above.
   */
  home_improvement_cancel_sign_owner1: string,
  home_improvement_cancel_sign_owner1_date: number,
  home_improvement_cancel_sign_owner2: string,
  home_improvement_cancel_sign_owner2_date: number,
  /**
   * MSE contract Scope of Work confirmation boxes (Jaron, 2026-08-25 meeting,
   * ticket 12915736128). `confirm_equipment_sign` is a full signature drawn
   * between the equipment list and the TOTAL ("Confirm Equipment", with a
   * "Confirm Total" caption beneath it). `confirm_total_initials` is the
   * customer's initials, captured through the signature pad in an "Initials"
   * modal, shown next to the TOTAL. Both are filled BY THE CUSTOMER during the
   * public signing flow (Luis, 2026-08-25: "Cliente es"), same as the Owner 1
   * signature — so, like it, they are not `readOnly`-gated.
   */
  home_improvement_confirm_equipment_sign: string,
  home_improvement_confirm_equipment_sign_date: number,
  home_improvement_confirm_total_initials: string,
  home_improvement_confirm_total_initials_date: number,
  /**
   * Signing v2 (ticket 13213629138): the customer signs the FINANCING
   * INFORMATION section of the M+S / CE contract. Customer-signed, like the
   * confirmation boxes above, so not `readOnly`-gated.
   */
  home_improvement_financing_info_sign: string,
  home_improvement_financing_info_sign_date: number,
  /**
   * No generic "rep phone" field existed anywhere in the schema before this
   * (MSE's dedicated Certificate of Completion, 2026-08-06) — homeRep is
   * name-only. Editable inline on that document since there was nowhere
   * else to set it.
   */
  home_improvement_rep_phone: string,
  /**
   * PROJECT LOCATION (MSE Home Improvement Agreement, 2026-08-07): reference
   * PDF wants Street/City/State/Zip as separate lines, but `customerAddress`
   * is one freeform string — seeded once from splitAddress(customerAddress)
   * when empty, editable afterward so a rep can fill in whatever the split
   * couldn't find (state/zip are often missing from the source address).
   */
  home_improvement_project_city: string,
  home_improvement_project_state: string,
  home_improvement_project_street: string,
  home_improvement_project_zip: string,
  //#endregion homeImprovementAgreement

  //#region securityContract
  security_completion_work_order: string,
  security_completion_date: string,
  security_completion_sign_customer: string,
  security_completion_sign_customer_date: number,
  //#endregion

  //#region signingV2
  /**
   * Signing v2 (Luis + Jaron meeting 2026-10-05). 'v2' once the customer
   * starts the guided flow or signs anything while still unsigned. The new
   * signature blocks render only for 'v2' or a customer with no signature at
   * all, so documents signed before keep their layout (V67). See
   * `common/helpers/signingFlowV2.ts`.
   */
  signing_flow_version: string,
  /**
   * Job Summary monitoring choice (ticket 13019071670): '' | 'with' | 'self'.
   * Only drives the Monthly Payment shown in the Job Summary; never
   * `security_monitoring_enabled`, never any TOTAL (V46).
   */
  job_summary_monitoring_choice: string,
  /**
   * "Other providers" comparison page (Jaron, 13312401562): 'yes' once the
   * rep turns it on. Name ('' = Brinks), equipment and issues one item per
   * line, monitoring per month ('' = $65), yearly inflation in percent
   * ('' = 3). See
   * `common/helpers/otherProviderComparison.ts`.
   */
  other_provider_compare: string,
  other_provider_name: string,
  other_provider_equipment: string,
  other_provider_issues: string,
  other_provider_monthly: string,
  other_provider_inflation: string,
  /**
   * Alarm tab equipment (13312401562): JSON list of rows, see
   * `equipmentRowsFor` in `common/helpers/otherProviderComparison.ts`. Wins
   * over `other_provider_equipment` once it holds a list.
   */
  other_provider_equipment_rows: string,
  /** Customer signature under the Job Summary monthly payment. */
  job_summary_payment_sign: string,
  job_summary_payment_sign_date: number,
  /** Monthly amount shown when `job_summary_payment_sign` was saved (V67). */
  job_summary_payment_signed_amount: number,
  /**
   * Customer signature on the Job Summary Monthly Payment line, for companies
   * that offer the monitoring choice (there `job_summary_payment_sign` sits
   * under the chosen option). Oct 5 meeting: two signatures.
   */
  job_summary_monthly_sign: string,
  job_summary_monthly_sign_date: number,
  /** One customer signature at the end of the NOTES pages. */
  job_summary_notes_sign: string,
  job_summary_notes_sign_date: number,
  //#endregion signingV2

  //#region smartHome
  //    smart_home: YesNo,
  /**
   * RowableInput.
   */
  //  smart_home_detail: string,
  //    smart_home_cost: string,
  //    smart_home_customer_cost: string,
  //    smart_home_notes: string,
  //    smart_home_extra_notes: string,
  //#endregion

  //#region glowQualify
  qualify_glow_first_name: string,
  qualify_glow_last_name: string,
  qualify_glow_phone_number: string,
  qualify_glow_email: string,
  qualify_glow_ssn: string,
  qualify_glow_birth_date: number,
  qualify_glow_street: string,
  qualify_glow_city: string,
  qualify_glow_state: string,
  qualify_glow_zip: string,

  incentive_glow_carbon_credits: Bit,
  incentive_glow_cash: Bit,
  incentive_glow_tax_deduction: Bit,
  incentive_glow_federal_tax_credit: Bit,
  incentive_adders_description: string,
  //#endregion glowQualify

  //#region custom
  custom_fields: YesNo,
  custom_fields_qty: number,
  custom_fields_cost: string,
  custom_fields_customer_cost: string,
  custom_fields_name: string,
  custom_fields_notes: string,
  custom_fields_show: Bit,
  //
  custom_two_fields: YesNo,
  custom_two_fields_qty: number,
  custom_two_fields_cost: string,
  custom_two_fields_customer_cost: string,
  custom_two_fields_name: string,
  custom_two_fields_notes: string,
  custom_two_fields_show: Bit,
  //
  custom_three_fields: YesNo,
  custom_three_fields_qty: number,
  custom_three_fields_cost: string,
  custom_three_fields_customer_cost: string,
  custom_three_fields_name: string,
  custom_three_fields_notes: string,
  custom_three_fields_show: Bit,
  //
  custom_four_fields: YesNo,
  custom_four_fields_qty: number,
  custom_four_fields_cost: string,
  custom_four_fields_customer_cost: string,
  custom_four_fields_name: string,
  custom_four_fields_notes: string,
  custom_four_fields_show: Bit,
  //
  custom_five_fields: YesNo,
  custom_five_fields_qty: number,
  custom_five_fields_cost: string,
  custom_five_fields_customer_cost: string,
  custom_five_fields_name: string,
  custom_five_fields_notes: string,
  custom_five_fields_show: Bit,
  //
  custom_six_fields: YesNo,
  custom_six_fields_qty: number,
  custom_six_fields_cost: string,
  custom_six_fields_customer_cost: string,
  custom_six_fields_name: string,
  custom_six_fields_notes: string,
  custom_six_fields_show: Bit,
  //
  custom_seven_fields: YesNo,
  custom_seven_fields_qty: number,
  custom_seven_fields_cost: string,
  custom_seven_fields_customer_cost: string,
  custom_seven_fields_name: string,
  custom_seven_fields_notes: string,
  custom_seven_fields_show: Bit,
  //
  custom_eight_fields: YesNo,
  custom_eight_fields_qty: number,
  custom_eight_fields_cost: string,
  custom_eight_fields_customer_cost: string,
  custom_eight_fields_name: string,
  custom_eight_fields_notes: string,
  custom_eight_fields_show: Bit,
  //
  custom_nine_fields: YesNo,
  custom_nine_fields_qty: number,
  custom_nine_fields_cost: string,
  custom_nine_fields_customer_cost: string,
  custom_nine_fields_name: string,
  custom_nine_fields_notes: string,
  custom_nine_fields_show: Bit,
  //
  custom_ten_fields: YesNo,
  custom_ten_fields_qty: number,
  custom_ten_fields_cost: string,
  custom_ten_fields_customer_cost: string,
  custom_ten_fields_name: string,
  custom_ten_fields_notes: string,
  custom_ten_fields_show: Bit,
  //#endregion custom
} as const

export type PaymentMethod = 'cash' | 'financing'

export type PayMethodApplied = u.r<{
  /** Only the alarm is financed in this mixed-deal snapshot. */
  pricingMode?: 'mixed-alarm-only'
  lockedKeys?: readonly string[]
  alarmLocked?: boolean
  finalCommission: number
  /**
   * Includes the commission, lead fee, and loan's dealer fee
   */
  grandTotalWDF: number
  loanCompanyName: string
  offerName: string
  paymentMethod: string
  planName: string
  subtotal: number
  termInterest: number
  termName: string
  termYears: number
  /**
   * Company product cost used by this loan; alarm-only in mixed mode.
   */
  totalCost: number
}>

const essentialFields: readonly CustomerField[] = [
  'has_touched_customer_cost',
  'has_touched_cost',
  'locked_price',
  'homeRep',
  'ssn',
  'dob',
  'id',
  'deleted',
  'keyID',
  'efficiency_updated',
  'solarCompany',
  'solarEmail',
  'solarRep',
  'yearHomeBuilt',
  'globalNotes',
  'createdAt',
  'objectID',
  'date',
  'key',
  'owner_house',
  'sort_key',
  'customerAddress',
  'customerEmail',
  'customerName',
  'customerPhone',
  'customerPhoneAlt',
  'products',
  'crm_id',
  'addressLat',
  'addressLng',
  'contractCompany',
  'coverCompany',
  'other_contract_company_name',
  'other_contract_company_address',
  'other_contract_company_phone',
  'other_contract_company_license',
  'other_contract_company_logo',
  'security_monitoring_enabled',

  'c_signature',
  'c_signature_backup',
  'c_signature_date',
  'report_company_signature',
  'report_company_signature_date',

  'main_panel_upgrade_installation_company',

  'battery',
  'battery_type',
  'battery_size',
  'battery_cost',
  'battery_customer_cost',
  'battery_notes',
  'battery_extra_notes',
  'battery_show',

  'roof_layover_or_tear',

  'attic_square_footage',
  'attic_insulation_type',

  'custom_fields',
  'custom_fields_qty',
  'custom_fields_cost',
  'custom_fields_customer_cost',
  'custom_fields_name',
  'custom_fields_notes',
  'custom_fields_show',
  'custom_two_fields',
  'custom_two_fields_qty',
  'custom_two_fields_cost',
  'custom_two_fields_customer_cost',
  'custom_two_fields_name',
  'custom_two_fields_notes',
  'custom_two_fields_show',
  'custom_three_fields',
  'custom_three_fields_qty',
  'custom_three_fields_cost',
  'custom_three_fields_customer_cost',
  'custom_three_fields_name',
  'custom_three_fields_notes',
  'custom_three_fields_show',
  'custom_four_fields',
  'custom_four_fields_qty',
  'custom_four_fields_cost',
  'custom_four_fields_customer_cost',
  'custom_four_fields_name',
  'custom_four_fields_notes',
  'custom_four_fields_show',
  'custom_five_fields',
  'custom_five_fields_qty',
  'custom_five_fields_cost',
  'custom_five_fields_customer_cost',
  'custom_five_fields_name',
  'custom_five_fields_notes',
  'custom_five_fields_show',
  'custom_six_fields',
  'custom_six_fields_qty',
  'custom_six_fields_cost',
  'custom_six_fields_customer_cost',
  'custom_six_fields_name',
  'custom_six_fields_notes',
  'custom_six_fields_show',
  'custom_seven_fields',
  'custom_seven_fields_qty',
  'custom_seven_fields_cost',
  'custom_seven_fields_customer_cost',
  'custom_seven_fields_name',
  'custom_seven_fields_notes',
  'custom_seven_fields_show',
  'custom_eight_fields',
  'custom_eight_fields_qty',
  'custom_eight_fields_cost',
  'custom_eight_fields_customer_cost',
  'custom_eight_fields_name',
  'custom_eight_fields_notes',
  'custom_eight_fields_show',
  'custom_nine_fields',
  'custom_nine_fields_qty',
  'custom_nine_fields_cost',
  'custom_nine_fields_customer_cost',
  'custom_nine_fields_name',
  'custom_nine_fields_notes',
  'custom_nine_fields_show',
  'custom_ten_fields',
  'custom_ten_fields_qty',
  'custom_ten_fields_cost',
  'custom_ten_fields_customer_cost',
  'custom_ten_fields_name',
  'custom_ten_fields_notes',
  'custom_ten_fields_show',
]

export const customEfficiencyField = [
  'custom_fields',
  'custom_two_fields',
  'custom_three_fields',
  'custom_four_fields',
  'custom_five_fields',
  'custom_six_fields',
  'custom_seven_fields',
  'custom_eight_fields',
  'custom_nine_fields',
  'custom_ten_fields',
] as const
export type CustomEfficiencyField = (typeof customEfficiencyField)[number]
export const isCustomEfficiencyField = (
  o: unknown,
): o is CustomEfficiencyField =>
  customEfficiencyField.includes(o as unknown as CustomEfficiencyField)

/**
 * Fields only used by the main company, not white labels.
 */
export const mainOnlyFields: readonly CustomerField[] = [
  'cash',
  'cash_cost',
  'cash_show',
  'solarEmail',
  'solar_tax',
  'solar_tax_cost',
  'solar_tax_customer_cost',
  'solar_tax_notes',
  'solar_tax_show',
  'glow',
  'glow_cost',
  'glow_customer_cost',
  'glow_price_per_watt',
  'glow_power_output_mw',
  'glow_notes',
  'glow_extra_notes',
  'glow_show',
  'security_tv',
  'security_tv_detail',
  'security_tv_cost',
  'security_tv_customer_cost',
  'security_tv_notes',
  'security_tv_extra_notes',
  //#region roofClaims
  'roof_third_party_policyholder',
  'roof_third_party_address',
  'roof_third_party_policy_number',
  'roof_third_party_claim_number',
  'roof_third_party_company',
  'roof_third_party_sign',
  'roof_third_party_sign_date',
  'roof_claiming_date',
  'roof_claiming_homeowner',
  'roof_claiming_phone',
  'roof_claiming_phone_alt',
  'roof_claiming_email',
  'roof_claiming_address',
  'roof_claiming_city',
  'roof_claiming_zip',
  'roof_claiming_sum',
  'roof_claiming_deductible',
  'roof_claiming_shingle_manufacturer',
  'roof_claiming_style',
  'roof_claiming_color',
  'roof_claiming_drip_color',
  'roof_claiming_age',
  'roof_claiming_layers',
  'roof_claiming_predominate_pitch',
  'roof_claiming_pipe_jack',
  'roof_claiming_chimney_flashing',
  'roof_claiming_chimney_cap',
  'roof_claiming_digital_satellite',
  'roof_claiming_gas_cap',
  'roof_claiming_other_one',
  'roof_claiming_turtle_vent',
  'roof_claiming_ridge_vent',
  'roof_claiming_turbine_vent',
  'roof_claiming_power_attic_vent',
  'roof_claiming_other_two',
  'roof_claiming_other_three',
  'roof_claiming_tear_off_layers_shingles',
  'roof_claiming_install_felt',
  'roof_claiming_close_valleys',
  'roof_claiming_ridges_color_coordinated',
  'roof_claiming_install_new_flashings',
  'roof_claiming_replace_ventilation',
  'roof_claiming_install_nails',
  'roof_claiming_clean_job_waste_gutters',
  'roof_claiming_two_year_workmanship_warranty',
  'roof_claiming_magnetic_sweep_property',
  'roof_claiming_notes',
  'roof_claiming_documents_notes',
  'roof_claiming_date_loss',
  'roof_claiming_insurance_company',
  'roof_claiming_claim',
  'roof_claiming_client_sign',
  'roof_claiming_client_sign_date',
  'roof_claiming_company_rep_sign',
  'roof_claiming_company_rep_sign_date',
  'roof_claiming_printed_name',
  'roof_claiming_completion_client_sign',
  'roof_claiming_completion_client_sign_date',
  'roof_claiming_company_contractor',
  //#endregion
]

export const glowFields: readonly CustomerField[] = [
  ...essentialFields,
  'air_conditioner',
  'air_conditioner_cost',
  'air_conditioner_current_tons',
  'air_conditioner_customer_cost',
  'air_conditioner_extra_notes',
  'air_conditioner_new_tons',
  'air_conditioner_notes',
  'air_conditioner_qty',
  'air_conditioner_replace_ducts',
  'air_conditioner_show',
  'air_conditioner_swamp_cooler_use_remove',
  'air_conditioner_swamp_cooler_use',
  'air_conditioner_unit_type',
  'air_conditioner_working',
  'airConditioner',

  'attic_insulation',
  'attic_insulation_cost',
  'attic_insulation_customer_cost',
  'attic_insulation_extra_notes',
  'attic_insulation_how_much',
  'attic_insulation_levels_type',
  'attic_insulation_notes',
  'attic_insulation_show',
  'attic_insulation_type',
  'attic_square_footage',
  'atticInsulation',

  'battery',
  'battery_cost',
  'battery_customer_cost',
  'battery_notes',
  'battery_show',
  'battery_size',
  'battery_type',

  'main_panel_upgrade',
  'main_panel_upgrade_cost',
  'main_panel_upgrade_customer_cost',
  'main_panel_upgrade_needed_or_requested',
  'main_panel_upgrade_installation_company',
  'main_panel_upgrade_notes',
  'main_panel_upgrade_show',

  'glow',
  'glow_cost',
  'glow_customer_cost',
  'glow_notes',
  'glow_extra_notes',
  'glow_power_output_mw',
  'glow_price_per_watt',
  'glow_show',

  'roof',
  'roofCheck',
  'roof_california_city',
  'roof_cost',
  'roof_fascia_included',
  'roof_fascia_included_square_footage',
  'roof_plywood_replaced',
  'roof_plywood_replaced_square',
  'roof_plywood_custom_roughly',
  'roof_layover_or_tear',
  'roof_patio_included',
  'roof_patio_how_many',
  'roof_customer_cost',
  'roof_layers_how_many',
  'roof_square_footage',
  'roof_extra_notes',
  'roof_notes',
  'roof_show',

  'custom_fields',
  'custom_fields_cost',
  'custom_fields_customer_cost',
  'custom_fields_name',
  'custom_fields_notes',
  'custom_fields_show',
  'custom_two_fields',
  'custom_two_fields_cost',
  'custom_two_fields_customer_cost',
  'custom_two_fields_name',
  'custom_two_fields_notes',
  'custom_two_fields_show',
  'custom_three_fields',
  'custom_three_fields_cost',
  'custom_three_fields_customer_cost',
  'custom_three_fields_name',
  'custom_three_fields_notes',
  'custom_three_fields_show',
  'custom_four_fields',
  'custom_four_fields_cost',
  'custom_four_fields_customer_cost',
  'custom_four_fields_name',
  'custom_four_fields_notes',
  'custom_four_fields_show',
  'custom_five_fields',
  'custom_five_fields_cost',
  'custom_five_fields_customer_cost',
  'custom_five_fields_name',
  'custom_five_fields_notes',
  'custom_five_fields_show',
  'custom_six_fields',
  'custom_six_fields_cost',
  'custom_six_fields_customer_cost',
  'custom_six_fields_name',
  'custom_six_fields_notes',
  'custom_six_fields_show',
  'custom_seven_fields',
  'custom_seven_fields_cost',
  'custom_seven_fields_customer_cost',
  'custom_seven_fields_name',
  'custom_seven_fields_notes',
  'custom_seven_fields_show',
  'custom_eight_fields',
  'custom_eight_fields_cost',
  'custom_eight_fields_customer_cost',
  'custom_eight_fields_name',
  'custom_eight_fields_notes',
  'custom_eight_fields_show',
  'custom_nine_fields',
  'custom_nine_fields_cost',
  'custom_nine_fields_customer_cost',
  'custom_nine_fields_name',
  'custom_nine_fields_notes',
  'custom_nine_fields_show',
  'custom_ten_fields',
  'custom_ten_fields_cost',
  'custom_ten_fields_customer_cost',
  'custom_ten_fields_name',
  'custom_ten_fields_notes',
  'custom_ten_fields_show',
]

export const nirvanaOnlyFields: readonly CustomerField[] = [
  'battery_multiple',
  'battery_multiple_bank_location',
  'battery_multiple_customer_material',
  'battery_multiple_distance_to_main_panel',
  'battery_multiple_how_many',
  'battery_multiple_cost',
  'battery_multiple_customer_cost',
  'battery_multiple_notes',
  'battery_multiple_extra_notes',
  'battery_multiple_show',

  'can_light_new',
  'can_light_new_how_many',
  'can_light_new_will_remove',
  'can_light_new_will_remove_how_many',
  'can_light_new_high_ceiling',
  'can_light_new_notes',
  'can_light_new_extra_notes',
  'can_light_new_cost',
  'can_light_new_customer_cost',
  'can_light_show',

  'can_light',
  'can_light_detail',
  'can_light_notes',
  'can_light_extra_notes',
  'can_light_cost',
  'can_light_customer_cost',
  'can_light_show',

  'ceiling_fan',
  'ceiling_fan_detail',
  'ceiling_fan_cost',
  'ceiling_fan_customer_cost',
  'ceiling_fan_notes',
  'ceiling_fan_extra_notes',
  'ceiling_fan_show',

  'checklist_nirvana_home_owners_info',
  'checklist_nirvana_product_pics',
  'checklist_nirvana_main_panel_pics',
  'checklist_nirvana_main_panel_inside_pics',
  'checklist_nirvana_utility_bill',

  'construction_work',
  'construction_work_notes',
  'construction_work_extra_notes',
  'construction_work_cost',
  'construction_work_customer_cost',
  'construction_work_show',

  'home_run',
  'home_run_ft',
  'home_run_cost',
  'home_run_customer_cost',
  'home_run_notes',
  'home_run_extra_notes',
  'home_run_show',

  'inverter_swap',
  'inverter_swap_qty',
  'inverter_swap_cost',
  'inverter_swap_customer_cost',
  'inverter_swap_customer_material',
  'inverter_swap_distance_to_mpu',
  'inverter_swap_size',
  'inverter_swap_notes',
  'inverter_swap_extra_notes',
  'inverter_swap_show',

  'nirvana_panels',
  'nirvana_panels_panel_scenario',
  'nirvana_panels_distance_to_main_panel',
  'nirvana_panels_how_many',
  'nirvana_panels_notes',
  'nirvana_panels_extra_notes',
  'nirvana_panels_cost',
  'nirvana_panels_customer_cost',
  'nirvana_panels_show',

  'outlet',
  'outlet_detail',
  'outlet_cost',
  'outlet_customer_cost',
  'outlet_notes',
  'outlet_extra_notes',
  'outlet_show',

  'protection',
  'protection_which',
  'protection_cost',
  'protection_customer_cost',
  'protection_notes',
  'protection_extra_notes',
  'protection_show',

  'report_nirvana_finance',
  'report_nirvana_upfront',
  'report_nirvana_remaining',

  'service_and_repair',
  'service_and_repair_hours',
  'service_and_repair_cost',
  'service_and_repair_customer_cost',
  'service_and_repair_notes',
  'service_and_repair_extra_notes',
  'service_and_repair_show',

  'small_system_extra_batteries_will',
  'small_system_size',
]

export const nirvanaFields: readonly CustomerField[] = [
  ...essentialFields.filter((f) => f !== 'solarRep'),

  ...nirvanaOnlyFields,

  'buildingType',
  'homeStoryNumber',
  'main_panel_location',

  'main_panel_upgrade',
  'main_panel_upgrade_rating',
  'main_panel_upgrade_cost',
  'main_panel_upgrade_customer_cost',
  'main_panel_upgrade_notes',
  'main_panel_upgrade_extra_notes',
  'main_panel_upgrade_show',

  'miniSplit',
  'mini_split',
  'mini_split_cost',
  'mini_split_customer_cost',
  'mini_split_ton1',
  'mini_split_ton2',
  'mini_split_tons',
  'mini_split_notes',
  'mini_split_extra_notes',
  'mini_split_show',

  'small_system',
  'smallSystem',
  'small_system_extra_batteries_qty',
  'small_system_extra_batteries_will',
  'small_system_size',
  'small_system_show',
  'small_system_customer_cost',
  'small_system_cost',
  'small_system_notes',
  'small_system_extra_notes',
  'small_system_show',

  'roof_shingles_or_tiles',

  'security_loan_applied',
  'payment_is_applied',
]

export const customFields = [
  'custom_fields',
  'custom_two_fields',
  'custom_three_fields',
  'custom_four_fields',
  'custom_five_fields',
  'custom_six_fields',
  'custom_seven_fields',
  'custom_eight_fields',
  'custom_nine_fields',
  'custom_ten_fields',
] as const

// TODO: Do this via database
if (env.IS_WHITE_LABEL) {
  if (env.CODENAME === 'glow') {
    const nonGlowFields = u
      .keys(CustomerSchema)
      .filter((field) => !glowFields.includes(field))
    for (const field of nonGlowFields) {
      delete CustomerSchema[field]
    }
  } else if (env.CODENAME === 'nirvana') {
    const nonNirvanaFields = u
      .keys(CustomerSchema)
      .filter((field) => !nirvanaFields.includes(field))
    for (const field of nonNirvanaFields) {
      delete CustomerSchema[field]
    }
  } else {
    for (const field of mainOnlyFields) {
      delete CustomerSchema[field]
    }
  }
} else {
  for (const field of nirvanaOnlyFields) {
    delete CustomerSchema[field]
  }
}

/**
 * Defines in compile-time the Customer type for use in compilation.
 */
export type Customer = typeof CustomerSchema

export type CustomerField = keyof Customer

export type CustomerFields = readonly CustomerField[]

export const customerFields: CustomerFields = u.keys(CustomerSchema)

export type CustomerValue = u.ValueOf<Customer>

export const multipleChoiceFields = [
  'air_conditioner_current_tons',
  'air_conditioner_new_tons',
  'air_conditioner_unit_type',
  'attic_insulation_type',
  'battery_size',
  'battery_type',
  'battery_multiple_bank_location',
  'buildingType',
  'homeRep',
  'homeStoryNumber',
  'inverter_swap_size',
  'main_panel_upgrade_needed_or_requested',
  'main_panel_upgrade_installation_company',
  'main_panel_upgrade_rating',
  'new_windows_california_city',
  'nirvana_panels_panel_scenario',
  'new_windows_color',
  'products',
  'protection_which',
  'roof_layover_or_tear',
  'roof_layers_how_many',
  'roof_tear_material',
  'roof_shingles_or_tiles',
  'security_cams_is_6',
  'small_system_size',
  'solarCompany',
  'solarRep',
] as const

export type MultipleChoiceField = (typeof multipleChoiceFields)[number]

export type TouchedCost = {
  [x in CustomerField]?: boolean
}

const customerFieldDropDown = [
  'air_conditioner_current_tons',
  'air_conditioner_tons_how_many',
  'air_conditioner_new_tons',
  'air_conditioner_unit_type',
  'battery_type',
  'battery_size',
  'homeRep',
  'main_panel_upgrade_needed_or_requested',
  'main_panel_upgrade_installation_company',
  'new_windows_california_city',
  'new_windows_color',
  'new_windows_sliding_glass_size',
  'roof_california_city',
  'roof_layers_how_many',
  'roof_layover_or_tear',
  'solarCompany',
  'solarRep',
] as const

export type CustomerFieldDropDown = (typeof customerFieldDropDown)[number]

export const isDropdownField = (o: unknown): o is CustomerFieldDropDown => {
  if (typeof o !== 'string') return false
  return customerFieldDropDown.includes(o as CustomerFieldDropDown)
}

interface ValidateCustomerRes {
  /**
   * Will be true if no error was found.
   */
  ok: boolean
  /**
   * Will be a populated string if any general error occurs while validating.
   */
  err: string
  /**
   * Will be populated if any error with an specific field was found.
   */
  errMap: u.Writable<{
    [K in keyof Customer]?: string
  }>
  /**
   * If ok is `true`, contains the customer in a correct data format.
   */
  sanitized: Customer
}

/**
 * Per-field sanitisers, keyed by the validator identity the schema declares.
 *
 * 12800619368: this used to be eight sequential `if (validator === X)` checks
 * inside the loop, none of them `else if`, so every one of the 576 fields
 * evaluated all eight regardless of which matched. A Map lookup does the same
 * work in one step. Output is unchanged - only one branch could ever match,
 * since `validator` is a single value.
 */
const VALIDATOR_SANITIZERS = new Map<unknown, (val: unknown) => unknown>([
  [string, sanitizeString],
  [StrToPrimitive, sanitizeStrToPrimitive],
  [boolean, sanitizeBoolean],
  [YesNo, sanitizeYesNo],
  [NoYes, sanitizeNoYes],
  [YesNoEmpty, sanitizeYesNoEmpty],
  [Bit, sanitizeBit],
  [number, sanitizeNumber],
])

/** Shared by all ten `custom_*_fields_qty` fields. */
const qtyOrOne = (val: unknown): number => (typeof val === 'number' ? val : 1)

/**
 * The value an untouched field of each validator type starts out as, used by
 * `createEmptyCustomer` below.
 *
 * `StrToPrimitive` is deliberately absent: its default is a fresh `{}`, and a
 * single one held here would be copied by reference into every customer ever
 * created, aliasing those fields across all of them.
 */
const VALIDATOR_DEFAULTS = new Map<unknown, unknown>([
  [number, 0],
  [string, ''],
  [boolean, false],
  [YesNo, 'no'],
  [NoYes, 'yes'],
  [YesNoEmpty, ''],
  [Bit, 1],
])

/** The two fields that need a fresh object per customer. See above. */
const STR_TO_PRIMITIVE_FIELDS: readonly (keyof Customer)[] = u
  .entries(CustomerSchema)
  .filter(([, validator]) => validator === StrToPrimitive)
  .map(([k]) => k)

/**
 * Every field at its starting value, computed once (Monday 12800619368).
 *
 * This was a per-call loop over all 576 schema fields running eight
 * `validator === X` comparisons each. Nothing in it depends on the arguments
 * `createEmptyCustomer` receives, so it only ever needed computing once.
 */
const EMPTY_CUSTOMER_DEFAULTS: Readonly<Record<string, unknown>> = (() => {
  const defaults: Record<string, unknown> = {}
  for (const [key, validator] of u.entries(CustomerSchema)) {
    if (VALIDATOR_DEFAULTS.has(validator)) {
      defaults[key as string] = VALIDATOR_DEFAULTS.get(validator)
    }
  }
  return defaults
})()

/**
 * Fields that need something beyond their validator, keyed by field name.
 *
 * These were eighteen `if (k === 'someField')` checks in the same loop, so
 * every field paid for all eighteen string comparisons to find the one that
 * might apply to it - or, for most fields, none at all.
 *
 * Each entry receives the raw customer and the result being built, because
 * two of them deliberately write to a DIFFERENT key than the one being
 * iterated (`new_windows` and `mini_split`). Those writes can be overwritten
 * later by their own key's turn in the loop, which is behaviour that depends
 * on CustomerSchema's key order - so the loop order below is preserved
 * exactly as it was.
 */
const FIELD_OVERRIDES: Record<
  string,
  (customer: Record<string, unknown>, result: u.Writable<Customer>) => void
> = {
  createdAt(customer, result) {
    result.createdAt = u.isNumber(customer['createdAt'] as string)
      ? u.normalizeTimestampToMs(customer['createdAt'] as number)
      : u.normalizeTimestampToMs(0)
  },
  custom_eight_fields_qty(customer, result) {
    result.custom_eight_fields_qty = qtyOrOne(
      customer['custom_eight_fields_qty'],
    )
  },
  air_conditioner_qty(customer, result) {
    result.air_conditioner_qty = qtyOrOne(customer['air_conditioner_qty'])
  },
  custom_fields_qty(customer, result) {
    result.custom_fields_qty = qtyOrOne(customer['custom_fields_qty'])
  },
  custom_five_fields_qty(customer, result) {
    result.custom_five_fields_qty = qtyOrOne(customer['custom_five_fields_qty'])
  },
  custom_four_fields_qty(customer, result) {
    result.custom_four_fields_qty = qtyOrOne(customer['custom_four_fields_qty'])
  },
  custom_nine_fields_qty(customer, result) {
    result.custom_nine_fields_qty = qtyOrOne(customer['custom_nine_fields_qty'])
  },
  custom_seven_fields_qty(customer, result) {
    result.custom_seven_fields_qty = qtyOrOne(
      customer['custom_seven_fields_qty'],
    )
  },
  custom_six_fields_qty(customer, result) {
    result.custom_six_fields_qty = qtyOrOne(customer['custom_six_fields_qty'])
  },
  custom_ten_fields_qty(customer, result) {
    result.custom_ten_fields_qty = qtyOrOne(customer['custom_ten_fields_qty'])
  },
  custom_three_fields_qty(customer, result) {
    result.custom_three_fields_qty = qtyOrOne(
      customer['custom_three_fields_qty'],
    )
  },
  custom_two_fields_qty(customer, result) {
    result.custom_two_fields_qty = qtyOrOne(customer['custom_two_fields_qty'])
  },
  customerPhone(_customer, result) {
    result.customerPhone = u.normalizePhoneNumber(result.customerPhone)
  },
  customerPhoneAlt(_customer, result) {
    result.customerPhoneAlt = u.normalizePhoneNumber(result.customerPhoneAlt)
  },
  mini_split(_customer, result) {
    result.mini_split_tons = JSON.stringify([newMiniSplit()])
  },
  new_windows(_customer, result) {
    result.new_windows_sqft_each_window = JSON.stringify([newWindowGroup()])
  },
  new_windows_color(_customer, result) {
    result.new_windows_color = 'White'
  },
  release_liability_date(customer, result) {
    // @ts-ignore preserved from the original
    result.release_liability_date =
      customer['release_liability_date'] || u.normalizeTimestampToMs(Date.now())
  },
  roof_tear_material(customer, result) {
    // @ts-ignore preserved from the original
    result.roof_tear_material = customer['roof_tear_material'] || '30'
  },
}

const sanitizeCustomer = (customer: Record<string, unknown>): Customer => {
  const result = {} as u.Writable<Customer>

  for (const [k, validator] of u.entries(CustomerSchema)) {
    // CAST: not sure what's going on here
    result[k] = customer[k] as never

    const sanitizer = VALIDATOR_SANITIZERS.get(validator)
    if (sanitizer) {
      // CAST: not sure what's going on here
      result[k] = sanitizer(customer[k]) as never
    }

    const override = FIELD_OVERRIDES[k]
    if (override) {
      override(customer, result)
    }
  }

  return result
}

export const validateCustomer = (
  o: Record<string, unknown>,
): ValidateCustomerRes => {
  const result: ValidateCustomerRes = {
    ok: true,
    err: '',
    errMap: {},
    sanitized: {} as Customer,
  }

  try {
    if (typeof o !== 'object' || o === null) {
      throw new TypeError('Not an object')
    }

    const template = createEmptyCustomer({
      customerName: o['customerName'] as string,
      firebaseKey: (o['key'] ||
        o['objectID'] ||
        o['id'] ||
        o['keyID']) as string,
      customerAddress: '',
      customerPhone: '',
      customerPhoneAlt: '',
      ecohomeRep: '',
      // CAST: There should be one company at least
      solarCompany: env.COMPANY_VALUES[0]!,
      solarRep: '',
    })

    result.sanitized = sanitizeCustomer({
      ...template,
      ...o,
    })

    if (result.sanitized.key === '') {
      result.errMap.key = 'Expected the key, got empty string'
      throw new ReferenceError(`Fatal: No customer key detected.`)
    }
    if (typeof result.sanitized.key === 'undefined') {
      result.errMap.key = 'Expected the key, got undefined'
      throw new ReferenceError(`Fatal: No customer key detected.`)
    }
    if (result.sanitized.key === null) {
      result.errMap.key = 'Expected the key, got null'
      throw new ReferenceError(`Fatal: No customer key detected.`)
    }
    // @ts-expect-error
    if (result.sanitized.key === true) {
      result.errMap.key = 'Expected the key, got true'
      throw new ReferenceError(`Fatal: No customer key detected.`)
    }
    // @ts-expect-error
    if (result.sanitized.key === false) {
      result.errMap.key = 'Expected the key, got false'
      throw new ReferenceError(`Fatal: No customer key detected.`)
    }
    if (typeof result.sanitized.key === 'object') {
      result.errMap.key = 'Expected the key, got object'
      throw new ReferenceError(`Fatal: No customer key detected.`)
    }
    if (result.sanitized.key === '[object Object]') {
      result.errMap.key = 'Expected the key, got [object Object]'
      throw new ReferenceError(`Fatal: No customer key detected.`)
    }

    // This step is a bit extraneous since the customer was already sanitized at
    // this point, but this helps keep validators and sanitizers in sync, thus
    // ensuring validators work as intended at write time as well.
    for (const [key, _validator] of Object.entries(CustomerSchema)) {
      const validator = _validator as unknown as (value: unknown) => boolean
      const value = (result.sanitized as Record<string, unknown>)[key]
      const isValid = validator(value)

      if (!isValid) {
        result.errMap[
          key as keyof Customer
        ] = `Expected ${validator.name}, got: ${value}`
      }
    }
  } catch (e) {
    result.err = u.processErr(e)
  } finally {
    if (result.err || Object.keys(result.errMap).length > 0) {
      result.ok = false

      u.log(
        `validateCustomer() -> Validation failed for customer ${o['key']} -> `,
        { ...result, sanitized: undefined },
      )
    }

    // eslint-disable-next-line no-unsafe-finally
    return result
  }
}

export const createEmptyCustomer = ({
  buildingType,
  firebaseKey,
  ecohomeRep,
  crm_id,
  customerName,
  customerAddress,
  customerPhone,
  customerPhoneAlt,
  main_panel_upgrade_installation_company,
  products,
  solarCompany,
  solarRep,
}: {
  buildingType?: string
  firebaseKey: string
  ecohomeRep?: string
  crm_id?: string
  customerName?: string
  customerAddress?: string
  customerPhone?: string
  customerPhoneAlt?: string
  main_panel_upgrade_installation_company?: string
  products?: string
  solarCompany?: string
  solarRep?: string
}): Customer => {
  // 12800619368: this used to be a loop evaluating eight `validator === X`
  // comparisons for each of the 576 fields, on every call. Its result depends
  // only on CustomerSchema, so it is computed once at module load
  // (EMPTY_CUSTOMER_DEFAULTS) and copied here instead.
  const result = { ...EMPTY_CUSTOMER_DEFAULTS } as u.Writable<Customer>

  // Excluded from the shared defaults on purpose: a single {} copied by
  // reference would alias these two fields across every customer ever
  // created. The original built a fresh object per call and so does this.
  for (const k of STR_TO_PRIMITIVE_FIELDS) {
    result[k] = {} as never
  }

  // NOT stamped here, and the comment that used to sit on this line was the
  // whole bug: "this function is the only place a deal is born [...] so a
  // customer arriving from Firebase never passes through here". It is not.
  // `createEmptyCustomer` is the generic EMPTY-CUSTOMER TEMPLATE, and four
  // read paths spread it under a record coming out of Firebase:
  // `validateCustomer` below, the `receivedSimpleCustomers` and
  // `receivedSingleCustomerFirebaseEvents` reducers in redux/customers.ts, and
  // the `makeSelectCustomer`/`makeSelectCustomerField` fallbacks. A legacy
  // record has no `commission_split_threshold` key of its own, so the stamp
  // survived into all of them and every deal sold before 2026-09-03 was paid
  // at the new $750 threshold instead of the $1,250 it was sold under.
  //
  // Zero means "sold before this field existed" - see `commissionSplitThreshold`
  // in helpers/commission.ts, which owns that rule. The stamp for a REAL new
  // deal is applied at the one place a deal is actually born and written to
  // Firebase: `app/screens/Customer/Customer.tsx`, right after this call.
  result.commission_split_threshold = 0
  result.lead_fee_stamped = 0
  result.commission_rules_date = 0

  result.buildingType = buildingType || ''
  result.homeRep = ecohomeRep || 'n/a'
  result.crm_id = crm_id || ''
  result.customerName = customerName || ''
  result.customerAddress = customerAddress || ''
  result.customerPhone = customerPhone || ''
  result.customerPhoneAlt = customerPhoneAlt || ''
  result.main_panel_upgrade_installation_company =
    main_panel_upgrade_installation_company || 'To Be Decided'
  result.solarCompany = solarCompany || ''
  result.solarRep = solarRep || 'n/a'
  result.key = firebaseKey
  result.id = firebaseKey
  result.keyID = firebaseKey
  result.createdAt = u.normalizeTimestampToMs(Date.now())
  result.date = u.normalizeTimestampToMs(Date.now())
  result.sort_key = u.normalizeTimestampToMs(Date.now()) * -1
  result.owner_house = ''

  result.roof_claiming_homeowner = customerName || ''
  result.roof_claiming_phone = customerPhone || ''

  result.roof_claiming_printed_name = customerName || ''

  result.new_windows_color = 'White'

  result.release_liability_date = u.normalizeTimestampToMs(Date.now())

  result.products = products || ''

  result.air_conditioner_qty = 1
  result.custom_fields_qty = 1
  result.custom_two_fields_qty = 1
  result.custom_three_fields_qty = 1
  result.custom_four_fields_qty = 1
  result.custom_five_fields_qty = 1
  result.custom_six_fields_qty = 1
  result.custom_seven_fields_qty = 1
  result.custom_eight_fields_qty = 1
  result.custom_nine_fields_qty = 1
  result.custom_ten_fields_qty = 1

  return result
}

export interface SimpleCustomer {
  customerAddress: string
  customerName: string
  /**
   * Unique identifier.
   */
  key: string
  sort_key: number
  /**
   * Alias for key.
   */
  objectID: string
  /**
   * This is just the state pretty much at this point, certain old customers
   * might have `RC` or `CPS_HIL` (for Home Improvement Loan) but that is now
   * deprecated. See `contractCompany`.
   */
  solarCompany: string
  solarRep: string
  deleted?: boolean
  homeRep?: string
  date?: number

  /**
   * @danlugo92: Avoids typescript accepting a Customer in place for a SimpleCustomer which
   * could be a mistake of our part.
   */
  _zSimple: null
}

export type GlowType = 'glow_silver' | 'glow_gold'

export const glowTypeToLabel: u.DeepReadonly<Record<GlowType, string>> = {
  glow_gold: 'Glow Gold',
  glow_silver: 'Glow Silver',
}

export type GlowStatus = 'activated' | 'pending' | 'submitted'

export interface Glower extends SimpleCustomer {
  readonly glowStatus: GlowStatus
}

export const glowStatusToLabel: Readonly<Record<GlowStatus, string>> = {
  activated: 'Activated',
  pending: 'Pending',
  submitted: 'Submitted',
}

export const simplifyCustomer = (customer: Customer): SimpleCustomer => {
  // if (typeof customer.key !== 'string') {
  //   throw new Error('No key in customer supplied to simplifyCustomer()')
  // }
  return {
    customerAddress: customer.customerAddress,
    customerName: customer.customerName,
    key: customer.key,
    objectID: customer.key || customer.objectID,
    solarCompany: customer.solarCompany,
    solarRep: customer.solarRep,
    sort_key: customer.sort_key,
    deleted: customer.deleted,
    homeRep: customer.homeRep,
    date: customer.date,
    _zSimple: null,
  }
}

export type MediaKind =
  | 'adders'
  | 'hvac_system'
  | 'electrical'
  | 'electricity'
  | 'id'
  | 'insulation'
  | 'owner_house'
  | 'problem'
  | 'roof'
  | 'temperature'
  | 'thermal'
  | 'other_documents'
  | 'solar_proposal'
  | 'all_exterior_walls_site_survey'
  | 'rafters_site_survey'
  | 'electrical_panel_site_survey'
  | 'all_planes_roof_site_survey'
  | 'other_site_survey'
  | 'house360_site_survey'
  | 'attic_site_survey'
  | 'roof_site_survey'
  | 'main_service_panel_site_survey'
  | 'eight_foot_msp_site_survey'
  | 'utility_meter_closeup_site_survey'
  | 'main_panel_label_site_survey'
  | 'main_panel_breakers_dead_front_included_site_survey'
  | 'main_panel_breakers_dead_front_removed_site_survey'
  | 'main_breaker_disconnect_closeup_site_survey'
  | 'mini_split_surroundings'
  | 'ground_system_connected_site_survey'
  | 'sub_panels_site_survey'
  | 'existing_solar_system_site_survey'
  | 'all_appliances_and_labels_site_survey'
  | 'windows'
  | 'rc_front_house'
  | 'rc_address_verification'
  | 'rc_3d_view'
  | 'rc_solar_panels'
  | 'rc_wires_home'
  | 'rc_siding'
  | 'rc_other_property_wind_damage'
  | 'rc_wind_damages'
  | 'rc_layers'
  | 'rc_roof_inclination'
  | 'rc_roof_overview'
  | 'rc_gutters'
  | 'rc_flashings'
  | 'rc_penetrations'
  | 'rc_hail'
  | 'rc_length_shingle'
  | 'rc_drip_edge'
  | 'rc_shingles_color'
  | 'rc_garage'
  | 'rc_ice_shield'
  | 'security_cam0'
  | 'security_cam1'
  | 'security_cam2'
  | 'security_cam3'
  | 'security_cam4'
  | 'security_cam5'
  | 'security_cam6'
  | 'security_cam7'
  | 'security_cctv'
  // B124: photo section for the signed cancellation letter (Jaron, Aug 6).
  // Unprefixed because it lives under Documentation with the other documents,
  // not under Alarms with the security_* rows.
  | 'cancellation_letter'
  | 'security_camera_locations'
  // Split out of 'security_camera_locations' (12896840378, Jaron 2026-09-07:
  // "Keep cameras" + "Add S4/S3/S2") — the original row keeps its existing
  // photos (87 across 30 customers), these three are new/empty alongside it.
  | 'security_camera_locations_s4'
  | 'security_camera_locations_s3'
  | 'security_camera_locations_s2'
  // 13019073213, Jaron 2026-09-10/13: new row alongside S4/S3/S2, same
  // additive pattern - "Don't know how I didn't think of that before".
  | 'security_camera_locations_indoor'
  | 'security_doorbell_locations'
  | 'security_360_house_view'
  | 'security_attic'
  | 'security_router_location'
  | 'security_post_camera_installation'
  | 'security_post_alarm_installation'
  | 'testimonial_video'
  // 12790174732: screen + front-camera recording of an in-person contract
  // signing, captured natively in the app's signing WebView (iOS only).
  | 'signing_recording'

export type MediaKinds = readonly MediaKind[]

export const mediaKinds: Record<MediaKind, MediaKind> = {
  adders: 'adders',
  hvac_system: 'hvac_system',
  all_exterior_walls_site_survey: 'all_exterior_walls_site_survey',
  all_planes_roof_site_survey: 'all_planes_roof_site_survey',
  attic_site_survey: 'attic_site_survey',
  electrical_panel_site_survey: 'electrical_panel_site_survey',
  electrical: 'electrical',
  electricity: 'electricity',
  house360_site_survey: 'house360_site_survey',
  id: 'id',
  insulation: 'insulation',
  other_documents: 'other_documents',
  other_site_survey: 'other_site_survey',
  owner_house: 'owner_house',
  problem: 'problem',
  rafters_site_survey: 'rafters_site_survey',
  roof_site_survey: 'roof_site_survey',
  all_appliances_and_labels_site_survey:
    'all_appliances_and_labels_site_survey',
  eight_foot_msp_site_survey: 'eight_foot_msp_site_survey',
  existing_solar_system_site_survey: 'existing_solar_system_site_survey',
  ground_system_connected_site_survey: 'ground_system_connected_site_survey',
  main_breaker_disconnect_closeup_site_survey:
    'main_breaker_disconnect_closeup_site_survey',
  main_panel_breakers_dead_front_included_site_survey:
    'main_panel_breakers_dead_front_included_site_survey',
  main_panel_breakers_dead_front_removed_site_survey:
    'main_panel_breakers_dead_front_removed_site_survey',
  main_panel_label_site_survey: 'main_panel_label_site_survey',
  main_service_panel_site_survey: 'main_service_panel_site_survey',
  mini_split_surroundings: 'mini_split_surroundings',
  sub_panels_site_survey: 'sub_panels_site_survey',
  utility_meter_closeup_site_survey: 'utility_meter_closeup_site_survey',
  roof: 'roof',
  solar_proposal: 'solar_proposal',
  temperature: 'temperature',
  thermal: 'thermal',
  windows: 'windows',
  rc_front_house: 'rc_front_house',
  rc_address_verification: 'rc_address_verification',
  rc_3d_view: 'rc_3d_view',
  rc_solar_panels: 'rc_solar_panels',
  rc_wires_home: 'rc_wires_home',
  rc_siding: 'rc_siding',
  rc_other_property_wind_damage: 'rc_other_property_wind_damage',
  rc_wind_damages: 'rc_wind_damages',
  rc_layers: 'rc_layers',
  rc_roof_inclination: 'rc_roof_inclination',
  rc_roof_overview: 'rc_roof_overview',
  rc_gutters: 'rc_gutters',
  rc_flashings: 'rc_flashings',
  rc_penetrations: 'rc_penetrations',
  rc_hail: 'rc_hail',
  rc_length_shingle: 'rc_length_shingle',
  rc_drip_edge: 'rc_drip_edge',
  rc_shingles_color: 'rc_shingles_color',
  rc_garage: 'rc_garage',
  rc_ice_shield: 'rc_ice_shield',

  security_cam0: 'security_cam0',
  security_cam1: 'security_cam1',
  security_cam2: 'security_cam2',
  security_cam3: 'security_cam3',
  security_cam4: 'security_cam4',
  security_cam5: 'security_cam5',
  security_cam6: 'security_cam6',
  security_cam7: 'security_cam7',
  security_cctv: 'security_cctv',
  cancellation_letter: 'cancellation_letter',

  security_camera_locations: 'security_camera_locations',
  security_camera_locations_s4: 'security_camera_locations_s4',
  security_camera_locations_s3: 'security_camera_locations_s3',
  security_camera_locations_s2: 'security_camera_locations_s2',
  security_camera_locations_indoor: 'security_camera_locations_indoor',
  security_doorbell_locations: 'security_doorbell_locations',
  security_360_house_view: 'security_360_house_view',
  security_attic: 'security_attic',
  security_router_location: 'security_router_location',
  security_post_camera_installation: 'security_post_camera_installation',
  security_post_alarm_installation: 'security_post_alarm_installation',
  testimonial_video: 'testimonial_video',
  signing_recording: 'signing_recording',
}

export const mainOnlyMedia: readonly MediaKind[] = ['adders']

export const glowMedia: readonly MediaKind[] = [
  'roof',
  'roof_site_survey',
  'electrical',
  'electrical_panel_site_survey',
  'roof',
  'roof_site_survey',
  'insulation',
  'id',
  'hvac_system',
  'owner_house',
  'other_documents',
]

export const nirvanaMedia: MediaKinds = [
  'electrical',
  'id',
  'mini_split_surroundings',
  'owner_house',
  'roof',
]

if (env.IS_WHITE_LABEL) {
  if (env.CODENAME === 'glow') {
    const allKinds = u.keys(mediaKinds)
    const nonGlowKinds = allKinds.filter((field) => !glowMedia.includes(field))
    for (const field of nonGlowKinds) {
      delete mediaKinds[field]
    }
  } else {
    for (const mediaKind of mainOnlyMedia) {
      delete mediaKinds[mediaKind]
    }
  }
}

export type MediaItemStatus = 'draft' | 'pending' | 'uploading' | 'uploaded'

export const isMediaItemStatus = (s: unknown): s is MediaItemStatus => {
  if (typeof s !== 'string') return false
  return ['pending', 'uploading', 'uploaded'].includes(s)
}

export const normalizeCheckShows: Partial<
  Record<keyof Customer, keyof Customer>
> = {
  attic_insulation: 'attic_insulation_show',
  battery: 'battery_show',
  duct_seal: 'duct_seal_show',
  roof: 'roof_show',
  new_windows: 'new_windows_show',
  air_conditioner: 'air_conditioner_show',
  main_panel_upgrade: 'main_panel_upgrade_show',
  mini_split: 'mini_split_show',
  derate: 'derate_show',
  smart_thermostat: 'smart_thermostat_show',
  attic_fan: 'attic_fan_show',
  pool_pump: 'pool_pump_show',

  duct_repair: 'duct_repair_show',
  duct_insulation: 'duct_insulation_show',

  cash: 'cash_show',
  solar_tax: 'solar_tax_show',
  small_system: 'small_system_show',
  flat_roof_panels: 'flat_roof_panels_show',
  panel_removal: 'panel_removal_show',
  custom_fields: 'custom_fields_show',
  custom_two_fields: 'custom_two_fields_show',
  custom_three_fields: 'custom_three_fields_show',
  custom_four_fields: 'custom_four_fields_show',
  custom_five_fields: 'custom_five_fields_show',
  custom_six_fields: 'custom_six_fields_show',
  custom_seven_fields: 'custom_seven_fields_show',
  custom_eight_fields: 'custom_eight_fields_show',
  custom_nine_fields: 'custom_nine_fields_show',
  custom_ten_fields: 'custom_ten_fields_show',
}

export interface MediaItem {
  /**
   * User-inputted description.
   */
  description: string
  /**
   * File height.
   */
  height?: number
  /**
   * Empty string if there's no errors. This should also hold any error that
   * happened when trying to upload.
   */
  err: string
  /**
   * Empty if it's a video.
   */
  image_of: string
  /**
   * File name.
   */
  name: string
  /**
   * The customer's id.
   */
  order_id: string
  /**
   * Local path inside device. Used when uploading or retrying uploading.
   * Useless afterwards.
   */
  path: string
  /**
   * Upload progress percentage (0–100). Transient — not persisted to Firebase.
   */
  progress?: number
  /**
   * Might not be present in older media in database, sanitize before using.
   */
  status: MediaItemStatus
  /**
   * Reduced quality base64.
   */
  thumbnail: string
  /**
   * Unix timestamp.
   */
  updated_at: number
  /**
   * URL constructed by backend API, will not be present if API is not being
   * used.
   */
  url: string
  /**
   * Empty if it's a photo.
   */
  video_of: string
  /**
   * File width.
   */
  width?: number
}

export type MediaItems = readonly MediaItem[]

export const processMediaItem = (maybeMediaItem: unknown): MediaItem => {
  const mi = maybeMediaItem as MediaItem

  const res: MediaItem = {
    description: '',
    err: '',
    image_of: '',
    name: '',
    order_id: '',
    path: '',
    status: 'uploaded',
    thumbnail: '',
    updated_at: 0,
    url: '',
    video_of: '',
  }

  try {
    if (maybeMediaItem === null || typeof maybeMediaItem !== 'object') {
      throw new TypeError('Not an object')
    }

    // Mandatory stuff

    if (typeof mi.image_of === 'string' && mi.image_of.length > 3) {
      res.image_of = mi.image_of
    } else if (typeof mi.video_of === 'string' && mi.video_of.length > 3) {
      res.video_of = mi.video_of
    } else {
      throw new TypeError(
        'image_of / video_of not an string or too short of a string',
      )
    }
    if (typeof mi.name === 'string' && mi.name.length > 5) {
      res.name = mi.name
    } else {
      throw new TypeError('name not a string or too short of a string')
    }
    if (typeof mi.order_id === 'string' && mi.order_id.length > 5) {
      res.order_id = mi.order_id
    } else {
      throw new TypeError('order_id not a string or too short')
    }

    // Optional stuff
    res.description = mi.description || ''
    res.err = typeof mi.err === 'string' ? mi.err : ''
    res.status = isMediaItemStatus(mi.status) ? mi.status : 'uploaded'
    res.updated_at =
      typeof mi.updated_at === 'number' ? mi.updated_at : Date.now()
    res.url = mi.url || ''
    res.thumbnail = mi.thumbnail || ''
    // Already uploaded stuff doesn't need the path anyways
    res.path = (typeof mi.path === 'string' && mi.path) || ''
  } catch (e: unknown) {
    u.log(e)
    u.log(maybeMediaItem)
    res.err = u.processErr(e)
  }

  return res
}

export const extractMediaKind = (mediaItem: MediaItem): MediaKind => {
  const { image_of, video_of } = mediaItem

  const mediaKindOfImage = image_of.replace('_pictures', '') as MediaKind
  const mediaKindOfVideo = video_of.replace('_videos', '') as MediaKind

  if (mediaKinds[mediaKindOfImage] || mediaKinds[mediaKindOfVideo]) {
    return mediaKindOfImage || mediaKindOfVideo
  }

  throw new TypeError(
    `Could not extract media kind from item: ${JSON.stringify(
      mediaItem,
      null,
      2,
    )}`,
  )
}

export const cashbackLabel: Partial<Record<keyof Customer, string>> = {
  cash_pool_pumps: 'Pool Pumps',
  cash_heat_pumps: 'Heat Pumps',
  cash_whole_house_fans_or_ventilation: 'Whole House Fans / Ventilation',
  cash_efficient_boilers: 'Efficient Boilers',
  cash_geothermal_heat_pumps: 'Geothermal Heat Pumps',
  cash_radiant_floors: 'Radiant Floors',
  cash_water_saving_fixtures: 'Water Saving Fixtures',
  cash_solar_water_heaters: 'Solar Water Heaters',
  cash_on_demand_or_tankless_hot: 'On Demand / Tankless Hot Water Heater',
  cash_efficient_water_heaters: 'Efficient Water Heaters',
  cash_artificial_turf: 'Artificial Turf',
  cash_irrigation_systems: 'Irrigation Systems',
  cash_windows: 'Windows',
  cash_floor_insulation: 'Floor Insulation',
  cash_air_conditioner: 'Air Conditioner',
}

export type Selector = {
  label: string
  onChange: () => void
  opts: u.Opts
  value: string
}

export const cashBackOptions = (): u.Opts =>
  u.keys(cashbackLabel).map((key) => ({
    value: key,
    label: cashbackLabel[key as keyof Customer] as string,
  }))

/**
 * There are no more separate HIL/RC "companies", easiest way to unify was just
 * give them all the same label (e.g. "California") and treat them as "state".
 */
const _companyOptions: u.Opts = u.zipWith(
  env.COMPANY_LABELS,
  env.COMPANY_VALUES,
  (label, value) => ({
    label,
    value,
  }),
)

/**
 * As of the time of this comment, the "normal" companies (non HIL/RC) are
 * specificized first in the env, and uniqBy() will conserve those and discard
 * the HIL/RC ones which is the desired behavior.
 */
export const companyOptions: u.Opts = uniqBy(_companyOptions, 'label')

export const convertToOptions = (
  optionsStr: unknown[] | unknown | u.Primitive | undefined,
): u.Opts => {
  const options = JSON.parse(
    typeof optionsStr === 'string' ? optionsStr : '[]',
  ) as string[]

  return u.zipWith(options, options, (label, value) => ({
    label,
    value,
  }))
}

export const acTons: u.Opts = [
  { value: '5', label: '5T' },
  { value: '4', label: '4T' },
  { value: '3.5', label: '3.5T' },
  { value: '3', label: '3T' },
  { value: '2.5', label: '2.5T' },
  { value: '2', label: '2T' },
]

export const acSplitUnit: u.Opts = [
  { value: 'roof_gas', label: 'Roof' },
  { value: 'split_gas', label: 'Split Gas' },
  { value: 'split_electric', label: 'Split Electric' },
]

export const airConditionerTons: u.Opts = [
  { value: '5T Split Unit Electric', label: '5T Split Unit Electric' },
  { value: '5T Split Unit Gas', label: '5T Split Unit Gas' },
  { value: '5T Roof', label: '5T Roof' },
  { value: '4T Split Unit Electric', label: '4T Split Unit Electric' },
  { value: '4T Split Unit Gas', label: '4T Split Unit Gas' },
  { value: '4T Roof', label: '4T Roof' },
  { value: '3.5T Split Unit Electric', label: '3.5T Split Unit Electric' },
  { value: '3.5T Split Unit Gas', label: '3.5T Split Unit Gas' },
  { value: '3.5T Roof', label: '3.5T Roof' },
  { value: '3T Split Unit Electric', label: '3T Split Unit Electric' },
  { value: '3T Split Unit Gas', label: '3T Split Unit Gas' },
  { value: '3T Roof', label: '3T Roof' },
  { value: '2.5T Split Unit Electric', label: '2.5T Split Unit Electric' },
  { value: '2.5T Split Unit Gas', label: '2.5T Split Unit Gas' },
  { value: '2.5T Roof', label: '2.5T Roof' },
  { value: '2T Split Unit Electric', label: '2T Split Unit Electric' },
  { value: '2T Split Unit Gas', label: '2T Split Unit Gas' },
  { value: '2T Roof', label: '2T Roof' },
  { value: 'Mini Split System 1Ton', label: 'Mini Split System 1Ton' },
  { value: 'Mini Split System 2Ton', label: 'Mini Split System 2Ton' },
]

export const areasDuctsOptions: u.Opts = [
  { value: 'Whole house', label: 'Whole house' },
  { value: 'Custom', label: 'Custom' },
]

// TODO: Use this enum everywhere
export enum RoofWorkType {
  Layover = 'Layover',
  /**
   * Used to be called "Tear off", now renamed to "Re Roof (Shingles)"
   */
  ReRoofShingles = 'Tear off',
  TileRepl = 'Tile Replacement',
  UnderlaymentRepl = 'Underlayment Replacement',
  ReRoofFlatRoof = 'Re Roof (Flat roof)',
  ReRoofWoodShake = 'Re Roof (Woodshake)',
}

export const roofWorkTypes: u.Opts = [
  { label: 'Re Roof (Shingles)', value: RoofWorkType.ReRoofShingles },
  { label: RoofWorkType.Layover, value: RoofWorkType.Layover },
  { label: RoofWorkType.TileRepl, value: RoofWorkType.TileRepl },
  {
    label: RoofWorkType.UnderlaymentRepl,
    value: RoofWorkType.UnderlaymentRepl,
  },
  { label: RoofWorkType.ReRoofFlatRoof, value: RoofWorkType.ReRoofFlatRoof },
  { label: RoofWorkType.ReRoofWoodShake, value: RoofWorkType.ReRoofWoodShake },
]

export const californiaCity: u.Opts = [
  { value: 'fresno', label: 'Fresno' },
  { value: 'victorville', label: 'Southern California' },
]

export const mainPanelUpgradeNeededOrRequested: u.Opts = [
  { value: 'needed', label: ' If Needed' },
  { value: 'requested', label: 'Requested' },
  { value: 'relocation', label: 'Relocation / Remodel' },
]

export type SlidingDoorsSizeKey =
  | 'Sliding Doors (5 ft)'
  | 'Sliding Doors (6 ft)'
  | 'Sliding Doors (8 ft)'

export const slidingToSize = {
  'Sliding Doors (5 ft)': 5,
  'Sliding Doors (6 ft)': 6,
  'Sliding Doors (8 ft)': 8,
}

export const slidingDoorsSize: u.Opts = [
  { value: 'Sliding Doors (5 ft)', label: '5 ft' },
  { value: 'Sliding Doors (6 ft)', label: '6 ft' },
  { value: 'Sliding Doors (8 ft)', label: '8 ft' },
]

export const miniSplitSystems: u.Opts =
  env.CODENAME === 'nirvana'
    ? [
        {
          label: 'Mini Split 9k BTU (650 sqft)',
          value: 'mini_split_9kBTU',
        },
        {
          label: 'Mini Split 12k BTU (750 sqft)',
          value: 'mini_split_12kBTU',
        },
        {
          label: 'Mini Split 18k BTU (1000 sqft)',
          value: 'mini_split_18kBTU',
        },
        {
          label: 'Mini Split 24k BTU (1400 sqft)',
          value: 'mini_split_24kBTU',
        },
      ]
    : [
        { value: 'mini_split_1t', label: 'Mini Split System 1T' },
        { value: 'mini_split_2t', label: 'Mini Split System 2T' },
      ]

export const fourLayerRoofOpts: u.Opts = [
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: '4', label: '4' },
]

export const roofMaterialDurations: u.Opts = [
  {
    label: '20 Years',
    value: '20',
  },
  {
    label: '30 Years',
    value: '30',
  },
  {
    label: '50 Years',
    value: '50',
  },
]

export const roofMaterialDurationsOnly30: u.Opts = [
  {
    label: '30 Years',
    value: '30',
  },
]

export const threeLayerRoofOpts: u.Opts = [
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
]

export interface DebtPayment {
  consolidation_debt_id: string
  debt: string
  id: string
  payment: string
}

export type DebtPayments = DebtPayment[]

export interface Collection {
  consolidation_debt_id: string
  id: string
  value: string
}

export type Collections = Collection[]
export interface ConsolidationDebtData {
  address: string
  closer: string
  creditScore: string
  id: string
  name: string
  notes: string
}

export type roofPlywoodOption = 'As needed' | 'Whole Roof'

// #region Rowable
// export interface SmartHomeGroup {
//   readonly id: string
//   readonly notes: string
//   /**
//    *  Will be injected at run-time.
//    */
//   readonly price: number
//   /**
//    * `'Door lock'`, `'Smart thermostat'`, etc.
//    */
//   readonly type: string
//   readonly qty: number
// }
export interface OutletGroup {
  readonly id: string
  readonly qty: string
  readonly relocationFeet: string
  readonly room: string
}

export const newOutletGroup = (): OutletGroup => ({
  id: uuid(),
  qty: '0',
  /**
   * Multi input, one value for each outlet.
   */
  relocationFeet: '[]',
  room: '',
})

export interface CanLightGroup {
  readonly ceilingHeight: string
  readonly id: string
  readonly room: string
  readonly qty: number
}

export const newCanLightGroup = (): CanLightGroup => ({
  ceilingHeight: '',
  id: uuid(),
  room: '',
  qty: 0,
})

export interface CeilingFan {
  readonly id: string
  readonly notes: string
  readonly type: string
}

export const newCeilingFan = (): CeilingFan => ({
  id: uuid(),
  notes: '',
  type: '',
})

/**
 * Security system group.
 */
export interface SecSysGroup {
  readonly id: string
  readonly notes: string
  /**
   *  Will be injected at run-time.
   */
  readonly price: number
  /**
   * `'Alarm panel'`, `'Smoke alarm'`, etc.
   */
  readonly type: string
  readonly qty: number
}

export type SecSysGroups = readonly SecSysGroup[]

export const newSecSysGroup = (): SecSysGroup => ({
  id: uuid(),
  notes: '',
  price: 0,
  qty: 1,
  type: '',
})

export interface CamGroup {
  readonly id: string
  /**
   * Cameras will have notes for each individual instance. Including doorbell.
   * Stringified to JSON string array, e.g. `["NotesA", "NotesB"]`.
   */
  readonly notes: string
  /**
   *  Will be injected at run-time.
   */
  readonly price: number
  readonly type: string
  readonly qty: number
}

export type CamGroups = readonly CamGroup[]

export const newCamGroup = (): CamGroup => ({
  id: uuid(),
  notes: '',
  price: 0,
  qty: 1,
  type: '',
})

export interface SecurityTV {
  readonly id: string
  /**
   *  Will be injected at run-time
   */
  readonly price: number
  /**
   * Will always be `1`, included for compatibility with applyLoanToSecurity()
   */
  readonly qty: number
  readonly type: string
}

export type SecurityTVs = readonly SecurityTV[]

export const newSecurityTV = (): SecurityTV => ({
  id: uuid(),
  price: 0,
  qty: 1,
  type: '',
})

export type Rowable =
  | CanLightGroup
  | CamGroup
  | CeilingFan
  | MiniSplit
  | OutletGroup
  | SecSysGroup
  | SecurityTV
  | SlidingGlassDoorGroup
  | WindowGroup

export const rowableFields = [
  'can_light_detail',
  'ceiling_fan_detail',
  'mini_split_tons',
  'new_windows_sliding_glass_sqft',
  'new_windows_sqft_each_window',
  'outlet_detail',
  'security_cams_detail',
  'security_home_automation_detail',
  'security_system_detail',
  'security_tv_detail',
  'security_wifi_cameras_detail',
] as const

export type FieldRowable = (typeof rowableFields)[number]

export interface WindowGroup {
  /**
   * Default is White. Including all windows sold without color until now.
   */
  readonly color: string
  /**
   * Unique ID for each window group.
   */
  readonly id: string
  /**
   * Sqft.
   */
  readonly size: number
  /**
   * Quantity of windows of this size.
   */
  readonly window: number
  /**
   * Giovanni only, old customers.
   */
  readonly windowType: string
}

export const newWindowGroup = (): WindowGroup => ({
  color: 'White',
  id: uuid(),
  size: 0,
  window: 1,
  windowType: 'standard',
})

export const isWindowGroup = (o: unknown): o is WindowGroup => {
  if (typeof o !== 'object' || o === null) {
    return false
  }

  const obj = o as WindowGroup

  return (
    typeof obj.id === 'string' &&
    (typeof obj.size === 'number' || typeof obj.size === 'string') &&
    (typeof obj.window === 'number' || typeof obj.window === 'string')
  )
}

export interface SlidingGlassDoorGroup {
  readonly id: string
  readonly size: string
  readonly total: number
}

export const newSlidingGlassDoorGroup = (): SlidingGlassDoorGroup => ({
  id: uuid(),
  size: '',
  total: 1,
})

export const newMiniSplit = (): MiniSplit => {
  const ms: MiniSplit = {
    id: uuid(),
    tons: '',
    qty: 1,
  }

  if (env.CODENAME === 'nirvana') {
    ;(ms as u.Writable<MiniSplit>).distanceToCondenser = 0
    ;(ms as u.Writable<MiniSplit>).location = ''
  }

  return ms
}

export interface CalcCol {
  /**
   * A unique key is needed.
   */
  readonly key: string
  /** e.g. 'amt*price' */
  readonly calc: (item: unknown) => number
  readonly label: string
  readonly type: 'calc'
  readonly widthPer: number
}
export interface InputCol {
  readonly key: string
  readonly label: string
  readonly type: 'input'
  readonly widthPer: number
}
/**
 * Will render several inputs based on the `qty` value on the row.
 */
export interface MultiInputCol {
  readonly key: string
  readonly label: string
  readonly type: 'multi-input'
  readonly widthPer: number
}
export interface NotesInputCol {
  readonly key: string
  readonly label: string
  readonly type: 'notes-input'
  readonly widthPer: number
}
export interface NumberInputCol {
  readonly key: string
  readonly label: string
  readonly type: 'number-input'
  readonly widthPer: number
}
export interface OptCol {
  readonly key: string
  readonly label: string
  readonly opts: u.Opts
  readonly type: 'opts'
  /**
   * Percentage of width. It should add up to exactly 90%, the remaining 10%
   * is for the delete button
   */
  readonly widthPer: number
}
/**
 * The percentages of width for all column should add up to exactly 90%, the
 * remaining 10% is for the delete button. Any overflow will be rendered on a
 * second line.
 */
export type Col =
  | CalcCol
  | InputCol
  | MultiInputCol
  | NotesInputCol
  | NumberInputCol
  | OptCol

export type ColType = Col['type']
export type Cols = readonly Col[]

export const initSlidingGlassDoorGroupCols: Cols = [
  {
    key: 'total',
    label: 'Doors',
    type: 'number-input',
    widthPer: 24,
  },
  {
    key: 'size',
    label: 'Size (ft)',
    opts: slidingDoorsSize,
    type: 'opts',
    widthPer: 50,
  },
  {
    key: 'sliding-total',
    calc: (item) => {
      const maybeSize =
        slidingToSize[
          (item as SlidingGlassDoorGroup).size as keyof typeof slidingToSize
        ]

      return (maybeSize || 0) * (item as SlidingGlassDoorGroup).total
    },
    label: 'Total (ft)',
    type: 'calc',
    widthPer: 16,
  },
]

export interface MiniSplit {
  /**
   * Nirvana only.
   */
  readonly distanceToCondenser?: number
  readonly id: string
  /**
   * Nirvana only.
   */
  readonly location?: string
  readonly qty: number
  readonly tons: string
}

export const initMiniSplitGroupCols: Cols = (() => {
  const cols: Col[] = [
    {
      key: 'tons' as const,
      label: 'Tons',
      opts: miniSplitSystems,
      type: 'opts',
      widthPer: 60,
    },
  ]

  if (env.CODENAME === 'upsmain') {
    cols.push({
      key: 'qty' as const,
      label: 'Qty',
      type: 'number-input',
      widthPer: 30,
    })
  }

  if (env.CODENAME === 'nirvana') {
    cols.push({
      key: 'distanceToCondenser' as const,
      label: 'Distance to condenser (ft)',
      type: 'input',
      widthPer: 15,
    })

    cols.push({
      key: 'location' as const,
      label: 'Location',
      type: 'input',
      widthPer: 25,
    })
  }

  return cols
})()

export const thirdWindowColWidth = 36

export const windowGroupCols: Cols = [
  {
    key: 'window',
    label: 'Windows',
    type: 'number-input',
    widthPer: 20,
  },
  {
    key: 'size',
    label: 'Size (Sqft)',
    type: 'input',
    widthPer: 22,
  },
  {
    key: 'color',
    label: 'Color',
    opts: [],
    type: 'opts',
    widthPer: thirdWindowColWidth,
  },
  {
    key: 'window-total',
    calc: (item) => {
      return (item as WindowGroup).window * (item as WindowGroup).size
    },
    label: 'Total',
    type: 'calc',
    widthPer: 12,
  },
]

export const windowTypeCol = {
  key: 'windowType',
  label: 'Window Option',
  opts: [
    {
      label: 'Elite',
      value: 'elite',
    },
    {
      label: 'Elite w/Grid',
      value: 'elite-grid',
    },
    {
      label: 'Standard',
      value: 'standard',
    },
    {
      label: 'Standard w/Grid',
      value: 'standard-grid',
    },
  ],
  type: 'opts',
  widthPer: thirdWindowColWidth,
} as const

export const windowGridCol = {
  key: 'isGrid',
}

export type RowableToCols = u.ReadonlyRecord<Cols, FieldRowable>
export const rowableToCols: RowableToCols = {
  can_light_detail: [
    {
      key: 'room',
      label: 'Room',
      type: 'input',
      widthPer: 55,
    },
    {
      key: 'ceilingHeight',
      label: 'Ceiling height',
      type: 'input',
      widthPer: 20,
    },
    {
      key: 'qty',
      label: 'Qty',
      type: 'number-input',
      widthPer: 15,
    },
  ],
  ceiling_fan_detail: [
    {
      key: 'type',
      label: 'Type',
      opts: [],
      type: 'opts',
      widthPer: 90,
    },
    {
      key: 'notes',
      label: 'Notes',
      type: 'notes-input',
      widthPer: 10,
    },
  ],
  mini_split_tons: initMiniSplitGroupCols,
  new_windows_sliding_glass_sqft: initSlidingGlassDoorGroupCols,
  new_windows_sqft_each_window: windowGroupCols,
  outlet_detail: [
    {
      key: 'room',
      label: 'Room',
      type: 'input',
      widthPer: 60,
    },
    {
      key: 'qty',
      label: 'Qty',
      type: 'number-input',
      widthPer: 30,
    },
    {
      key: 'relocationFeet',
      label: 'Relocation feet',
      type: 'multi-input',
      widthPer: 24,
    },
  ],
  /**
   * Keep this in sync with react-utils's useRowable replace.
   */
  security_cams_detail: [
    {
      key: 'type' as const,
      label: '',
      opts: u.EMPTY_ARRAY,
      type: 'opts',
      widthPer: 50,
    },
    {
      key: 'qty' as const,
      label: 'Qty',
      type: 'number-input',
      widthPer: 20,
    },
    {
      key: 'total',
      label: 'Total',
      type: 'calc',
      calc: (item) => (item as CamGroup).price * (item as CamGroup).qty,
      widthPer: 20,
    },
    {
      key: 'notes',
      label: 'Notes',
      type: 'notes-input',
      widthPer: 50,
    },
  ],
  /**
   * Keep this in sync with react-utils's useRowable replace.
   */
  security_system_detail: [
    {
      key: 'type',
      label: '',
      opts: u.EMPTY_ARRAY,
      type: 'opts',
      widthPer: 50,
    },
    {
      key: 'qty',
      label: 'Qty',
      type: 'number-input',
      widthPer: 20,
    },
    {
      key: 'total',
      label: 'Total',
      type: 'calc',
      calc: (item) => (item as SecSysGroup).price * (item as SecSysGroup).qty,
      widthPer: 20,
    },
    {
      key: 'notes',
      label: 'Notes',
      type: 'notes-input',
      widthPer: 50,
    },
  ],
  /**
   * Keep this in sync with react-utils's useRowable replace.
   */
  security_wifi_cameras_detail: [
    {
      key: 'type' as const,
      label: '',
      opts: u.EMPTY_ARRAY,
      type: 'opts',
      widthPer: 50,
    },
    {
      key: 'qty' as const,
      label: 'Qty',
      type: 'number-input',
      widthPer: 20,
    },
    {
      key: 'total',
      label: 'Total',
      type: 'calc',
      calc: (item) => (item as CamGroup).price * (item as CamGroup).qty,
      widthPer: 20,
    },
    {
      key: 'notes',
      label: 'Notes',
      type: 'notes-input',
      widthPer: 50,
    },
  ],
  /**
   * Keep this in sync with react-utils's useRowable replace.
   */
  security_home_automation_detail: [
    {
      key: 'type' as const,
      label: '',
      opts: u.EMPTY_ARRAY,
      type: 'opts',
      widthPer: 50,
    },
    {
      key: 'qty' as const,
      label: 'Qty',
      type: 'number-input',
      widthPer: 20,
    },
    {
      key: 'total',
      label: 'Total',
      type: 'calc',
      calc: (item) => (item as CamGroup).price * (item as CamGroup).qty,
      widthPer: 20,
    },
    {
      key: 'notes',
      label: 'Notes',
      type: 'notes-input',
      widthPer: 50,
    },
  ],
  /**
   * Keep this in sync with react-utils's useRowable replace.
   */
  security_tv_detail: [
    {
      key: 'type',
      label: 'Type',
      opts: u.EMPTY_ARRAY,
      type: 'opts',
      widthPer: 70,
    },
    {
      key: 'price-ui',
      label: 'Price',
      type: 'calc',
      calc: (item) => Number((item as SecSysGroup).price) || 0,
      widthPer: 20,
    },
  ],
}
//#endregion Rowable

interface TransactionData {
  key: string
  newValue: string
  oldValue: string
}

type TransactionCustomerData = Partial<Record<keyof Customer, TransactionData>>

export interface DBTransaction {
  customer: string
  data: TransactionCustomerData
  type: string
  uidUser: string
  user: string
}

export interface MediaData {
  /**
   * User-inputted description (`MediaItem.description`), rendered as a
   * caption under the thumbnail in the customer report's photo grid.
   */
  description?: string
  type: 'img' | 'video'
  url: string
}

export type InitialCustomer = u.DeepReadonly<{
  customerAddress: string
  customerName: string
  customerPhone: string
  customerPhoneAlt: string
  ecohomeRep: string
  firebaseKey: string
  homeRep: string
  solarCompany: string
  solarRep: string
}>

/**
 * Main only via createdAt.
 */
export const insulationTypesOld: u.ReadonlyRecord<readonly string[]> = {
  APS: ['R-18', 'R-30', 'R-38', 'R-60'],
  CPS: ['R-17', 'R-18', 'R-23', 'R-30'],
  NPS: ['R-12', 'R-18', 'R-19', 'R-30'],
  NMPS: ['R-18', 'R-38', 'R-49'],
  TPS: ['R-18', 'R-30', 'R-38', 'R-60'],
  UPS: ['R-18', 'R-38', 'R-49'],
}

export const NEW_INSULATION_TYPES_CUTOFF = moment('2023-09-18')

export interface CloserOrSetter {
  readonly label: string
  readonly value: string
}

/**
 * region Media Capture
 */

export interface PhotoFile {
  height: number
  isRawPhoto: boolean
  metadata?: {
    Orientation: number
    DPIHeight: number
    DPIWidth: number
  }
  path: string
  thumbnail?: Record<string, unknown>
  width: number
}
export interface MediaCapture {
  media: PhotoFile
  type: 'photo' | 'video'
}
export interface MediaCaptureWithBackup {
  description?: string
  id: string
  mediaCapture: MediaCapture
  oldMediaCapture?: MediaCapture
}

export interface SectionMediaCapture {
  data: MediaCaptureWithBackup[]
  title: MediaKind
}

export interface Debt {
  collections: string
  consolidationDebts: string
  created_at: number
  debtsPayments: string
  update_at: number
}
/**
 * endregion
 */
export type InvoiceCompany = 'trojan' | 'tello'

export interface InvoiceCompanyData {
  readonly address: string
  readonly license: string
  readonly name: string
}

export const companyInvoiceOptions: Record<InvoiceCompany, InvoiceCompanyData> =
  {
    tello: {
      address: '14310 Agave St\nMoreno Valley, CA 92553',
      license: '#886710',
      name: 'Tello Construction LLC',
    },
    trojan: {
      address: '311 S Valley View STE B103, Las Vegas Nevada',
      license: '#85060',
      name: 'Trojan Construction LLC',
    },
  }

export const solarCompanyToInvoiceCompanies: Partial<
  u.ReadonlyRecord<readonly InvoiceCompany[]>
> = {
  CPS: ['tello'],
  NPS: ['trojan'],
}

export interface Invoice {
  readonly company: InvoiceCompany
  readonly customerID: string
  readonly date: number
  readonly efficiencyInfo: string
  readonly invoiceNumber: number
  readonly lastErr: string
  readonly objectID: string
  readonly sort_key: number
  readonly status: 'error' | 'pending' | 'uploading' | 'uploaded'
}

export type SimpleInvoice = Omit<
  Invoice,
  'date' | 'invoiceNumber' | 'objectID' | 'sort_key' | 'status'
>

export const isInvoice = (o: unknown): o is Invoice => {
  return (
    typeof o === 'object' &&
    o !== null &&
    typeof (o as Record<string, unknown>)['invoiceNumber'] === 'number'
  )
}

// TODO: New Name InvoiceEfficiency
export interface InvoiceEfficiencyInfo {
  readonly amount: string
  readonly description: string
  readonly id: string
  readonly quantity: string
  readonly selectEfficiency: string
}

export const isInvoiceEfficiencyInfo = (
  o: unknown,
): o is InvoiceEfficiencyInfo => {
  if (typeof o !== 'object' || o === null) {
    return false
  }

  const obj = o as u.Dict<unknown>

  // TODO: better validation
  if (
    !obj['amount'] ||
    !obj['quantity'] ||
    !obj['selectEfficiency'] ||
    !obj['description'] ||
    !obj['id']
  ) {
    return false
  }
  return true
}

// The key is the Invoice ID
export type Invoices = u.Dict<Invoice>

export interface InvoiceUser {
  readonly email: string
  readonly date: number
}

export type InvoiceUsers = u.ReadonlyRecord<InvoiceUser>

export type FilteringQuery<T> = {
  column: keyof T
  query: string
}

export type SortQuery<T> = {
  column: keyof T
  kind: 'asc' | 'desc'
}

export const customerHasDataInField = (
  field: CustomerField,
  customer: Customer,
): boolean => {
  const fieldType = CustomerSchema[field]

  if (fieldType === number) {
    // @ts-ignore
    return customer[field] > 0
  }
  if (fieldType === string) {
    return customer[field] !== ''
  }
  if (fieldType === YesNo) {
    return customer[field] === 'Yes'
  }
  if (fieldType === NoYes) {
    return customer[field] === 'No'
  }
  if (fieldType === YesNoEmpty) {
    return customer[field] !== ''
  }
  if (fieldType === boolean) {
    return customer[field] === true
  }
  if (fieldType === Bit) {
    return customer[field] === 1
  }
  throw new TypeError(`Should be unreachable`)
}

export type RoofClaimingContractorCompany = 'ecohome'

export const RoofClaimingContractorToLabel: u.DeepReadonly<
  Record<RoofClaimingContractorCompany, string>
> = {
  ecohome: 'Eco Home Specialties',
}

export const RoofClaimingContractorHeaderText: u.DeepReadonly<
  Record<RoofClaimingContractorCompany, string[]>
> = {
  ecohome: [
    '(385) 277 0123',
    'Lic# 1110603',
    '19 E Citrus Ave. Suite 201',
    'Redlands, CA 92373',
  ],
}

export type PartialCustomer = u.Writable<Partial<Customer>>

// Partial<Record<keyof Customer, utils.ValueOf<Customer>> >

export const RoofClaimingContractData = {
  roof_claiming_date: number,
  roof_claiming_homeowner: string,
  roof_claiming_phone: string,
  roof_claiming_phone_alt: string,
  roof_claiming_email: string,
  roof_claiming_address: string,
  roof_claiming_city: string,
  roof_claiming_zip: string,
  roof_claiming_sum: string,
  roof_claiming_deductible: string,
  roof_claiming_shingle_manufacturer: string,
  roof_claiming_style: string,
  roof_claiming_color: string,
  roof_claiming_drip_color: string,

  roof_claiming_age: string,
  roof_claiming_layers: string,
  roof_claiming_predominate_pitch: string,

  roof_claiming_pipe_jack: string,
  roof_claiming_chimney_flashing: string,
  roof_claiming_chimney_cap: string,
  roof_claiming_digital_satellite: string,
  roof_claiming_gas_cap: string,
  roof_claiming_other_one: string,

  roof_claiming_turtle_vent: string,
  roof_claiming_ridge_vent: string,
  roof_claiming_turbine_vent: string,
  roof_claiming_power_attic_vent: string,
  roof_claiming_other_two: string,
  roof_claiming_other_three: string,
  //---- End Main Page inputs ----
  //---- Main Page checkbox ----
  roof_claiming_tear_off_layers_shingles: Bit,
  roof_claiming_install_felt: Bit,
  roof_claiming_close_valleys: Bit,
  roof_claiming_ridges_color_coordinated: Bit,
  roof_claiming_install_new_flashings: Bit,
  roof_claiming_replace_ventilation: Bit,
  roof_claiming_install_nails: Bit,
  roof_claiming_clean_job_waste_gutters: Bit,
  roof_claiming_two_year_workmanship_warranty: Bit,
  roof_claiming_magnetic_sweep_property: Bit,

  roof_claiming_notes: string,
  roof_claiming_documents_notes: string,

  roof_claiming_date_loss: number,
  roof_claiming_insurance_company: string,
  roof_claiming_claim: string,

  roof_claiming_client_sign: string,
  roof_claiming_client_sign_date: number,
  roof_claiming_company_rep_sign: string,
  roof_claiming_company_rep_sign_date: number,

  roof_claiming_printed_name: string,
  roof_claiming_completion_client_sign: string,
  roof_claiming_completion_client_sign_date: number,

  roof_claiming_company_contractor: string,
}

export const roofClaimingFields = u.keys(RoofClaimingContractData)

export type RoofClaimingContractDataType = typeof RoofClaimingContractData
export type Lang = 'ENG' | 'ES'

export type LangText = u.DeepReadonly<Record<string, string>>

export interface ExampleImage {
  readonly label?: string
  readonly source: number | string
}
export type ExampleImages = readonly ExampleImage[]

export type PhoneValidatorResponse = {
  phone: string
  valid: boolean
  format: {
    international: string
    local: string
  }
  country: {
    code: string
    name: string
    prefix: string
  }
  location: string
  type: string
  carrier: string
}

export type ACPricing = u.r<{
  new_install: u.DictB<string>
  replacement: u.DictB<string>
}>

export type ACPricingStates = u.r<{
  california: {
    fresno: ACPricing
    victorville: ACPricing
  }
  nevada: {
    all: ACPricing
  }
}>

export type ContractCompany = u.r<{
  address1: string
  address2: string
  // keyed by state name (e.g. "arizona", "nevada") — overrides address1 when present
  address1s?: u.ReadonlyRecord<string>
  // keyed by state name (e.g. "arizona", "nevada") — overrides address2 when present
  address2s?: u.ReadonlyRecord<string>
  /**
   * Used only for Nirvana as of right now.
   */
  email?: string
  logo: string
  longName: string
  // keyed by state name (e.g. "arizona", "nevada") — overrides longName when present
  longNames?: u.ReadonlyRecord<string>
  phoneNumber: string
  // keyed by state name (e.g. "arizona", "nevada") — overrides phoneNumber when present
  phoneNumbers?: u.ReadonlyRecord<string>
  /**
   * Originally for roofing work but now can be any kind of license related to
   * the company
   */
  roofingLicense: string
  // keyed by state name (e.g. "arizona", "nevada") — overrides roofingLicense when present
  roofingLicenses?: u.ReadonlyRecord<string>
  shortName: string
  /**
   * Comma-separated.
   */
  states: string
  /**
   * Optional — only HomeCam has this populated as of 2026-07-27 (Work Board
   * 12283794761). No per-state variant (single flat field) since Jaron only
   * gave one website for the whole company, unlike phoneNumbers/roofingLicenses.
   */
  website?: string
}>

export type ContractCompanies = u.ReadonlyRecord<ContractCompany>

export type MobileFile = u.r<{
  height: number
  path: string
  mime: string
  /**
   * If it's a video this is the path to the video while `path` contains the
   * thumbnail.
   */
  videoPath?: string
  width: number
}>

export const getCustomFieldsQties = (
  activeCustomFields: CustomerFields,
  customerData: Customer,
): u.numbers =>
  activeCustomFields.map(
    (field): number =>
      customerData[(field + '_qty') as CustomerField] as number,
  )

export const getCustomFieldsNames = (
  activeCustomFields: CustomerFields,
  customerData: Customer,
): u.strings =>
  activeCustomFields.map(
    (field): string =>
      customerData[(field + '_name') as CustomerField] as string,
  )

export const getCustomFieldsCosts = (
  activeCustomFields: CustomerFields,
  customerData: Customer,
): u.strings =>
  activeCustomFields.map(
    (field): string =>
      customerData[(field + '_customer_cost') as CustomerField] as string,
  )

export const getCustomFieldsNotes = (
  activeCustomFields: CustomerFields,
  customerData: Customer,
) =>
  activeCustomFields.map((field): u.strings => {
    // `|| ''` for the reason spelled out on `customerNotes` in helpers.ts: a
    // customer is the raw Firebase document, so a `_notes` key written before
    // the field existed is simply absent and `.split` throws. Inlined rather
    // than calling the helper because helpers.ts imports this module.
    const notes =
      (customerData[(field + '_notes') as CustomerField] as string) || ''

    return notes.split('\n').filter((_) => !!_)
  })
